import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import useAuthStore from '../../context/authStore';
import { Link, useLocation } from 'react-router-dom';
import { formatRoleLabel } from '../../utils/roles';
import { consultingServices } from '../../../../shared/consultingServices';
import { invoicesAPI, organizationsAPI, staffingAPI } from '../../services/api';
import { hasUserModuleAccess, CLIENT_PORTAL_MODULES } from '../../utils/clientPortalPermissions';

const lmsAudienceOptions = ['RN', 'LPN', 'CNA', 'Med Tech', 'Dietary', 'Administrative', 'All Staff'];
const consultingServiceOptions = consultingServices.map((service) => ({
  value: service.id,
  label: service.label,
  categories: (service.subcategories || []).map((subcategory) => subcategory.label),
}));
const consultingServiceCategories = consultingServiceOptions.reduce((acc, service) => {
  acc[service.value] = service.categories;
  return acc;
}, {});
const customCourseRequestTypes = [
  { value: 'new_custom_course', label: 'New Custom Course' },
  { value: 'policy_conversion', label: 'Policy / Procedure Conversion' },
  { value: 'update_existing_course', label: 'Update Existing Course' },
  { value: 'other', label: 'Other' },
];
const customCourseLengthOptions = [
  { value: 'basic_0_20', label: 'Up to 20 min (Basic)' },
  { value: 'standard_20_45', label: '20-45 min (Standard)' },
  { value: 'advanced_45_90', label: '45-90 min (Advanced)' },
  { value: 'other', label: 'Other' },
];
const customCourseObjectiveSlots = 5;

const parseAudienceList = (value) => String(value || '')
  .split(',')
  .map((entry) => entry.trim())
  .filter(Boolean);

const buildAudienceValue = (selected = [], other = '') => (
  [...selected, ...parseAudienceList(other)].filter(Boolean).join(', ')
);

const defaultServiceRequestForm = () => ({
  // Facility admins choose who pays for a change they request
  payer: 'facility',
  requestedServices: {
    consulting: 'no_change',
    staffing: 'no_change',
    lms: 'no_change',
  },
  consultingRequest: {
    facilityIds: [],
    serviceOffers: [{ type: '', categories: [] }],
    summary: '',
  },
  staffingRequest: {
    requestScopeType: 'all_active_facilities',
    facilityIds: [],
    summary: '',
  },
  lmsRequest: {
    standardAccess: false,
    customDevelopment: false,
    hostingType: 'stratel',
  },
  lmsScoping: {
    facilityIds: [],
    trainingAudience: '',
    estimatedSeats: '',
    trainingGoals: '',
    requiredTopics: '',
    needRecommendation: true,
    knownCoursesOrTopics: '',
    preferredPricingModel: 'undecided',
    preferredPaymentSchedule: 'undecided',
  },
  lmsCustomCourseRequest: {
    requestType: 'new_custom_course',
    courseTitle: '',
    currentSubscriptionType: '',
    targetAudience: [],
    targetAudienceOther: '',
    estimatedLearners: '',
    regulatoryDriver: '',
    siteSpecificPolicy: '',
    targetCourseLength: 'standard_20_45',
    rushRequired: false,
    preferredDeliveryDate: '',
    learningObjectives: Array.from({ length: customCourseObjectiveSlots }, () => ''),
    assetsProvided: {
      existingPolicyDocs: false,
      subjectMatterExpertAvailable: false,
      subjectMatterExpertName: '',
      trainingMaterialsAvailable: false,
      brandingAssetsAvailable: false,
      brandingAssetNotes: '',
      videoAvailable: false,
      complianceScenariosIncluded: false,
      complianceScenarioNotes: '',
    },
    additionalNotes: '',
    pricingAcknowledged: false,
  },
  reason: '',
});

const getServiceEntitlements = (...sources) => {
  for (const source of sources) {
    if (source?.serviceEntitlements) return source.serviceEntitlements;
    if (source?.billingOverview?.serviceEntitlements) return source.billingOverview.serviceEntitlements;
  }
  return {};
};

const buildConsultingRequestPrefill = (overviewData) => {
  const consulting = getServiceEntitlements(overviewData)?.consulting || {};
  const specificServices = Array.isArray(consulting.effectiveServices) ? consulting.effectiveServices : [];
  const grouped = new Map();

  consultingServiceOptions.forEach((service) => {
    const matchedCategories = service.categories.filter((category) => specificServices.includes(category));
    if (matchedCategories.length) {
      grouped.set(service.value, {
        type: service.value,
        categories: matchedCategories,
      });
    }
  });

  const serviceOffers = Array.from(grouped.values());
  return serviceOffers.length ? serviceOffers : [{ type: '', categories: [] }];
};

const buildServiceRequestFormFromOrganization = (organization) => {
  const base = defaultServiceRequestForm();
  const staffingCommercial = getServiceEntitlements(organization)?.staffing || {};
  const lmsCommercial = getServiceEntitlements(organization)?.lms || {};
  const currentPricingModel = String(lmsCommercial?.subscription?.pricingModel || '').trim();
  const currentPaymentSchedule = String(lmsCommercial?.subscription?.paymentSchedule || lmsCommercial?.subscription?.seatPaymentSchedule || '').trim();
  const currentSubscriptionType = [currentPricingModel, currentPaymentSchedule].filter(Boolean).join(' / ');

  return {
    ...base,
    consultingRequest: {
      ...base.consultingRequest,
      serviceOffers: buildConsultingRequestPrefill(organization?.billingOverview),
    },
    staffingRequest: {
      ...base.staffingRequest,
      requestScopeType:
        staffingCommercial?.coverage?.appliesTo === 'selected_facilities'
          ? 'selected_facilities'
          : staffingCommercial?.coverage?.appliesTo === 'organization'
            ? 'organization'
            : 'all_active_facilities',
      facilityIds: staffingCommercial?.coverage?.facilityIds || [],
    },
    lmsScoping: {
      ...base.lmsScoping,
      estimatedSeats: String(lmsCommercial?.usage?.seatsLimit || ''),
      preferredPricingModel: ['full_library', 'per_seat'].includes(currentPricingModel) ? currentPricingModel : 'undecided',
      preferredPaymentSchedule: ['annual', 'monthly'].includes(currentPaymentSchedule) ? currentPaymentSchedule : 'undecided',
    },
    lmsCustomCourseRequest: {
      ...base.lmsCustomCourseRequest,
      currentSubscriptionType,
      estimatedLearners: String(lmsCommercial?.usage?.seatsLimit || ''),
    },
  };
};

const formatLmsScopeLabel = (scopeType = 'facility_scoped') => (
  scopeType === 'org_wide' ? 'Org-wide' : 'Facility-scoped'
);

const formatLmsDeliveryLabel = (deliveryMode = 'stratel_hosted') => (
  deliveryMode === 'external_delivery' ? 'External delivery' : 'Stratel hosted'
);

const formatLmsCoverageLabel = (coverage = {}) => {
  if (!coverage) return '-';
  const scopeLabel = formatLmsScopeLabel(coverage.scopeType);
  const totalFacilityCount = Number(coverage.totalFacilityCount || 0);
  const coveredFacilityCount = Number(
    coverage.coveredFacilityCount || coverage.includedFacilityCount || 0,
  );
  if (coverage.scopeType === 'org_wide') {
    return totalFacilityCount > 0
      ? `${scopeLabel} · ${totalFacilityCount} of ${totalFacilityCount} facilities covered`
      : `${scopeLabel} · all facilities covered`;
  }
  if (totalFacilityCount > 0) {
    return `${scopeLabel} · ${coveredFacilityCount} of ${totalFacilityCount} facilities covered`;
  }
  return `${scopeLabel} · ${coveredFacilityCount || 1} facility${coveredFacilityCount === 1 ? '' : 'ies'} covered`;
};

const formatCommercialAppliesTo = (coverage = {}) => {
  if (!coverage) return '-';
  const appliesTo = String(coverage.appliesTo || '').trim();
  const includedFacilityCount = Number(coverage.includedFacilityCount || 0);
  if (appliesTo === 'all_active_facilities') return 'All active facilities';
  if (appliesTo === 'selected_facilities') {
    return includedFacilityCount > 0
      ? `Selected facilities (${includedFacilityCount})`
      : 'Selected facilities';
  }
  if (appliesTo === 'facility_count_only') {
    return includedFacilityCount > 0
      ? `${includedFacilityCount} facilit${includedFacilityCount === 1 ? 'y' : 'ies'} in commercial scope`
      : 'Facility count to be finalized';
  }
  return 'Organization-level';
};

const formatCommercialFacilityNames = (coverage = {}) => {
  const names = Array.isArray(coverage?.facilityNames)
    ? coverage.facilityNames.filter(Boolean)
    : [];
  if (names.length) return names.join(', ');
  if (coverage?.appliesTo === 'all_active_facilities') return 'All active facilities';
  if (coverage?.appliesTo === 'facility_count_only') return 'Facility list to be finalized during commercial review';
  return '-';
};

const formatLmsFacilityNames = (coverage = {}) => {
  const names = Array.isArray(coverage?.facilityNames)
    ? coverage.facilityNames.filter(Boolean)
    : [];
  return names.length ? names.join(', ') : '-';
};

const normalizeFacilityPolicy = (facility) => ({
  internalAccessPolicy: facility?.internalAccessPolicy || {
    lms: { assignmentMode: 'inherit', assignmentRoles: [], assignmentUserIds: [] },
    staffing: { requesterMode: 'inherit', requesterRoles: [], requesterUserIds: [], matchRejectionCap: undefined },
    consulting: { collaboratorMode: 'inherit', collaboratorRoles: [], collaboratorUserIds: [] },
    billing: {
      viewInvoicesFacilityPartyMode: 'inherit', viewInvoicesFacilityPartyRoles: [], viewInvoicesFacilityPartyUserIds: [],
      viewInvoicesOrgPartyMode: 'inherit', viewInvoicesOrgPartyRoles: [], viewInvoicesOrgPartyUserIds: [],
      viewPaymentStatusOrgPartyMode: 'inherit', viewPaymentStatusOrgPartyRoles: [], viewPaymentStatusOrgPartyUserIds: [],
      payInvoicesFacilityPartyMode: 'inherit', payInvoicesFacilityPartyRoles: [], payInvoicesFacilityPartyUserIds: [],
    },
    serviceRequests: { requestMode: 'inherit', requestRoles: [], requestUserIds: [] },
    rateApprovals: {
      accessMode: 'inherit', accessRoles: [], accessUserIds: [],
      approveMode: 'inherit', approveRoles: [], approveUserIds: [],
    },
  },
  billingAccessPolicy: facility?.billing?.accessPolicy || {
    enabled: false,
    allowFacilityAdminManagement: false,
    viewRoles: [],
    actRoles: [],
    userIds: [],
  },
});

const toBuffer = (value) => {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4 ? '='.repeat(4 - (normalized.length % 4)) : '';
  const raw = window.atob(normalized + pad);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const toBase64Url = (buffer) => {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer || []);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

export default function SettingsPage() {
  const { user, token } = useAuthStore();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [activeOrgSubtab, setActiveOrgSubtab] = useState('organization-settings');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // compute whether this user can administer full org settings; needed early
  const isOrgAdminSettings = hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'admin');
  const orgHeadingLabel = isOrgAdminSettings ? 'Organization Settings' : 'Facility Settings';


  // Role-based tabs
  const availableTabs = useMemo(() => {
    // default order for all users
    const baseTabs = ['profile', 'password', 'notifications', 'security'];
    if (
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') ||
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view') ||
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')
    ) {
      // move organization tab to front for clients so billing-related settings are easier to access
      return ['organization', ...baseTabs];
    }
    return baseTabs;
  }, [user?.role]);

  // Ensure activeTab is valid for role; if current tab was removed by role change,
  // fall back to whichever tab appears first in the availableTabs list.
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs]);

  // Deep-link support: /settings?tab=organization&orgTab=service-access
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const orgTab = params.get('orgTab');
    if (tab && availableTabs.includes(tab)) {
      setActiveTab(tab);
    }
    if (orgTab && ['organization-settings', 'service-access', 'internal-access-policy'].includes(orgTab)) {
      setActiveOrgSubtab(orgTab);
    }
  }, [location.search, availableTabs]);

  useEffect(() => {
    if (
      !hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'admin') &&
      !hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') &&
      !hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view') &&
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view') &&
      activeOrgSubtab !== 'service-access'
    ) {
      setActiveOrgSubtab('service-access');
    }
  }, [user, activeOrgSubtab]);
  
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || ''
  });

  // Organization state (for headquarters admin / facility admin service requests)
  const [organization, setOrganization] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [serviceRequestSaving, setServiceRequestSaving] = useState(false);
  const [serviceRequestHistory, setServiceRequestHistory] = useState([]);
  const [serviceRequestLoading, setServiceRequestLoading] = useState(false);
  const [serviceRequestForm, setServiceRequestForm] = useState(defaultServiceRequestForm);
  const [pendingOrgReviewCount, setPendingOrgReviewCount] = useState(0);
  const [orgDecisionSaving, setOrgDecisionSaving] = useState(null); // requestId being decided
  const [orgDecisionNote, setOrgDecisionNote] = useState(''); // shared note field
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [facilityPolicyDraft, setFacilityPolicyDraft] = useState(() => normalizeFacilityPolicy());
  const [savingFacilityPolicy, setSavingFacilityPolicy] = useState(false);

  // facility info (for facility-level settings)
  const [facilityInfoDraft, setFacilityInfoDraft] = useState({
    name: '',
    website: '',
    phone: '',
    address: { street: '', city: '', state: '', zip: '' },
    description: '',
    requireTimeApproval: true,
    requirements: {
      minExperience: '',
      credentialsRequired: null,
      orientation: { required: null, duration: '', paidOrientation: null },
    },
  });
  const [savingFacilityInfo, setSavingFacilityInfo] = useState(false);
  const [creatingFacility, setCreatingFacility] = useState(false);
  const [newFacilityDraft, setNewFacilityDraft] = useState({
    name: '',
    type: 'other',
    website: '',
    phone: '',
    address: { street: '', city: '', state: '', zip: '' },
    description: '',
  });

  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  // Notification preferences (per-type) and global toggles
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [globalPrefs, setGlobalPrefs] = useState({ email: true, inApp: true });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [showAdvancedNotifications, setShowAdvancedNotifications] = useState(false);
  const [security, setSecurity] = useState({
    twoFactor: { enabled: false, method: 'email' },
    passkeys: { enabled: false, count: 0 },
    email: user?.email || '',
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityBusy, setSecurityBusy] = useState(false);
  const [twoFactorMethodToEnable, setTwoFactorMethodToEnable] = useState('email');
  const [twoFactorChallengeId, setTwoFactorChallengeId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpSetupCode, setTotpSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyNickname, setPasskeyNickname] = useState('');
  const [disableTwoFactorPassword, setDisableTwoFactorPassword] = useState('');

  const clientNotificationGroups = [
    {
      title: 'Commercial',
      items: [
        { key: 'proposal_sent', label: 'Proposal sent', basic: true },
        { key: 'proposal_accepted', label: 'Proposal accepted', basic: true },
        { key: 'proposal_declined', label: 'Proposal declined' },
        { key: 'msa_sent', label: 'MSA sent', basic: true },
        { key: 'msa_accepted', label: 'MSA accepted', basic: true },
        { key: 'msa_declined', label: 'MSA declined' },
      ],
    },
    {
      title: 'Staffing',
      items: [
        { key: 'staffing_request_approved', label: 'Staffing request approved', basic: true },
        { key: 'staffing_request_declined', label: 'Staffing request declined' },
        { key: 'staffing_match_found', label: 'Worker match found', basic: true },
        { key: 'wage_tier_approval_requested', label: 'Wage approval requested', basic: true },
        { key: 'wage_tier_approved', label: 'Wage approval approved' },
        { key: 'wage_tier_declined', label: 'Wage approval declined' },
        { key: 'timesheet_submitted', label: 'Timesheet submitted' },
        { key: 'timesheet_approved', label: 'Timesheet approved' },
        { key: 'timesheet_rejected', label: 'Timesheet rejected' },
      ],
    },
    {
      title: 'Billing',
      items: [
        { key: 'invoice_issued', label: 'Invoice sent', basic: true },
        { key: 'invoice_viewed', label: 'Invoice viewed' },
        { key: 'invoice_paid', label: 'Invoice paid', basic: true },
        { key: 'invoice_overdue', label: 'Invoice overdue', basic: true },
        { key: 'payment_failed', label: 'Payment failed', basic: true },
      ],
    },
    {
      title: 'LMS',
      items: [
        { key: 'course_assigned', label: 'Course assigned', basic: true },
        { key: 'course_due_soon', label: 'Course due soon' },
        { key: 'course_overdue', label: 'Course overdue' },
        { key: 'course_completed', label: 'Course completed', basic: true },
        { key: 'certificate_issued', label: 'Certificate issued', basic: true },
      ],
    },
    {
      title: 'Messages',
      items: [
        { key: 'message_received', label: 'New message', basic: true },
        { key: 'message_mentioned', label: 'Mention in message thread' },
        { key: 'weekly_digest', label: 'Weekly digest', basic: true },
      ],
    },
    {
      title: isOrgAdminSettings ? 'Organization' : 'Facility',
      items: [
        { key: 'service_change_request_created', label: 'Service change request submitted' },
        { key: 'service_change_request_decided', label: 'Service change request decision', basic: true },
        { key: 'facility_service_change_pending_org_review', label: 'Facility service request awaiting your review' },
        { key: 'facility_service_change_org_decided', label: 'Organization decision on your service request', basic: true },
      ],
    },
  ];

  const updatePref = (type, field, value) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || {}),
        [field]: value,
      },
    }));
  };

  const normalizeAddress = (address) => {
    if (!address || typeof address !== 'object') {
      return { street: '', street2: '', city: '', state: '', zip: '', country: 'USA' };
    }
    return {
      street: address.street || '',
      street2: address.street2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      country: address.country || 'USA',
    };
  };

  const normalizeServices = (services) => {
    const base = {
      consulting: { enabled: false },
      staffing: { enabled: false },
      lms: { enabled: false },
    };
    if (!services || typeof services !== 'object') return base;
    return {
      consulting: { enabled: Boolean(services?.consulting?.enabled) },
      staffing: { enabled: Boolean(services?.staffing?.enabled) },
      lms: { enabled: Boolean(services?.lms?.enabled) },
    };
  };

  const normalizeServiceAccessPolicy = (policy) => {
    const allowedRoles = ['org_admin', 'org_team', 'facility_admin', 'facility_manager', 'facility_worker'];
    const cleanMode = (mode) => (mode === 'specific_users' ? 'specific_users' : 'role_based');
    const cleanIds = (ids) => (Array.isArray(ids) ? Array.from(new Set(ids.map((id) => String(id)))) : []);
    const cleanMatchRejectionCap = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return 3;
      return Math.max(0, Math.min(20, Math.floor(num)));
    };
    const cleanRoles = (roles, fallback) => {
      const input = Array.isArray(roles) ? roles.filter((r) => allowedRoles.includes(r)) : [];
      return input.length ? input : fallback;
    };
    return {
      lms: {
        assignmentMode: cleanMode(policy?.lms?.assignmentMode),
        assignmentRoles: cleanRoles(policy?.lms?.assignmentRoles, ['org_admin', 'facility_admin']),
        assignmentUserIds: cleanIds(policy?.lms?.assignmentUserIds),
      },
      staffing: {
        requesterMode: cleanMode(policy?.staffing?.requesterMode),
        requesterRoles: cleanRoles(policy?.staffing?.requesterRoles, ['org_admin', 'facility_admin']),
        requesterUserIds: cleanIds(policy?.staffing?.requesterUserIds),
        matchRejectionCap: cleanMatchRejectionCap(policy?.staffing?.matchRejectionCap),
      },
      consulting: {
        collaboratorMode: cleanMode(policy?.consulting?.collaboratorMode),
        collaboratorRoles: cleanRoles(policy?.consulting?.collaboratorRoles, ['org_admin', 'facility_admin', 'facility_manager']),
        collaboratorUserIds: cleanIds(policy?.consulting?.collaboratorUserIds),
      },
      billing: {
        accessMode: cleanMode(policy?.billing?.accessMode),
        accessRoles: cleanRoles(policy?.billing?.accessRoles, ['org_admin']),
        accessUserIds: cleanIds(policy?.billing?.accessUserIds),
        actionMode: cleanMode(policy?.billing?.actionMode),
        actionRoles: cleanRoles(policy?.billing?.actionRoles, ['org_admin']),
        actionUserIds: cleanIds(policy?.billing?.actionUserIds),
      },
      serviceRequests: {
        reviewFacilityMode: cleanMode(policy?.serviceRequests?.reviewFacilityMode),
        reviewFacilityRoles: cleanRoles(policy?.serviceRequests?.reviewFacilityRoles, ['org_admin']),
        reviewFacilityUserIds: cleanIds(policy?.serviceRequests?.reviewFacilityUserIds),
        approveFacilityMode: cleanMode(policy?.serviceRequests?.approveFacilityMode),
        approveFacilityRoles: cleanRoles(policy?.serviceRequests?.approveFacilityRoles, ['org_admin']),
        approveFacilityUserIds: cleanIds(policy?.serviceRequests?.approveFacilityUserIds),
        submitOrgMode: cleanMode(policy?.serviceRequests?.submitOrgMode),
        submitOrgRoles: cleanRoles(policy?.serviceRequests?.submitOrgRoles, ['org_admin']),
        submitOrgUserIds: cleanIds(policy?.serviceRequests?.submitOrgUserIds),
      },
      rateApprovals: {
        manageMode: cleanMode(policy?.rateApprovals?.manageMode),
        manageRoles: cleanRoles(policy?.rateApprovals?.manageRoles, ['org_admin']),
        manageUserIds: cleanIds(policy?.rateApprovals?.manageUserIds),
      },
      billingRules: {
        viewInvoicesFacilityPartyEnabled: policy?.billingRules?.viewInvoicesFacilityPartyEnabled !== false,
        viewInvoicesOrgPartyEnabled: Boolean(policy?.billingRules?.viewInvoicesOrgPartyEnabled),
        viewPaymentStatusOrgPartyEnabled: Boolean(policy?.billingRules?.viewPaymentStatusOrgPartyEnabled),
        payInvoicesFacilityPartyEnabled: policy?.billingRules?.payInvoicesFacilityPartyEnabled !== false,
      },
    };
  };

  const purchasedServiceEntries = (() => {
    const serviceMap = organization?.services || {};
    return [
      ['consulting', 'Consulting'],
      ['staffing', 'Staffing'],
      ['lms', 'LMS / Training'],
    ].filter(([key]) => Boolean(serviceMap?.[key]?.enabled));
  })();

  const { data: billingOverviewData } = useQuery({
    queryKey: ['settings-billing-overview', organization?._id],
    queryFn: () => invoicesAPI.getBillingOverview({}),
    select: (res) => res.data,
    enabled: activeTab === 'organization' && activeOrgSubtab === 'service-access' && Boolean(organization?._id) && hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.billing, 'view'),
  });
  const { data: orgFacilities = [], refetch: refetchOrgFacilities } = useQuery({
    queryKey: ['settings-org-facilities', organization?._id],
    queryFn: () => organizationsAPI.getMyFacilities(),
    select: (res) => Array.isArray(res?.data?.facilities) ? res.data.facilities : [],
    enabled: activeTab === 'organization' && Boolean(organization?._id) && (
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view') ||
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') ||
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')
    ),
  });
  // All org facilities are available for facility-level policy (no staffing-link filter).
  const policyFacilities = orgFacilities;
  const currentUserFacilityIds = useMemo(
    () => (
      Array.isArray(user?.clientStaffProfile?.facilityIds)
        ? user.clientStaffProfile.facilityIds.map((id) => String(id))
        : []
    ),
    [user?.clientStaffProfile?.facilityIds]
  );
  // orgFacilities is already scoped to the user's own facilities by the /me/facilities API
  // for facility-scoped roles, so no additional filtering is needed here.
  const serviceRequestFacilities = orgFacilities;
  const primaryScopedFacility = useMemo(() => {
    const matchingFacility = orgFacilities.find((facility) =>
      currentUserFacilityIds.includes(String(facility._id))
    );
    if (matchingFacility) return matchingFacility;
    if (!isOrgAdminSettings && currentUserFacilityIds.length > 0) {
      return {
        _id: currentUserFacilityIds[0],
        name: user?.clientStaffProfile?.facilityNames?.[0] || 'Assigned Facility',
        website: '',
        phone: '',
        address: { street: '', city: '', state: '', zip: '' },
        description: '',
      };
    }
    return orgFacilities[0] || null;
  }, [orgFacilities, currentUserFacilityIds, isOrgAdminSettings, user?.clientStaffProfile?.facilityNames]);

  const canEditFacilityInternalPolicy = useMemo(() => {
    // org_admin or facility_admin (limited to their accessible facilities) may edit facility policy.
    if (!hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'act')) {
      return false;
    }
    // A facility must be selected and must be in the user's accessible facility list.
    if (!selectedFacilityId) return false;
    return orgFacilities.some((facility) => String(facility._id) === String(selectedFacilityId));
  }, [user, selectedFacilityId, orgFacilities]);

  const canCreateFacility = useMemo(
    () =>
      Boolean(organization?._id) &&
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'act'),
    [organization?._id, user]
  );

  // Org admin can edit org-level internal access policy (LMS, Staffing, Consulting defaults).
  const canEditOrgInternalPolicy = useMemo(
    () => hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.internal_access_policy, 'act'),
    [user]
  );

  const { data: selectedOrgFacilityData, refetch: refetchSelectedOrgFacility } = useQuery({
    queryKey: ['settings-org-facility-detail', selectedFacilityId, orgFacilities.length],
    queryFn: async () => ({
      facility: orgFacilities.find((facility) => String(facility._id) === String(selectedFacilityId)) || null,
    }),
    enabled: activeTab === 'organization' && Boolean(selectedFacilityId),
  });
  const { data: selectedFacilityData, refetch: refetchSelectedFacility } = useQuery({
    queryKey: ['settings-facility-detail', selectedFacilityId],
    queryFn: () => staffingAPI.getFacility(selectedFacilityId),
    select: (res) => res.data,
    enabled: activeTab === 'organization' && activeOrgSubtab === 'internal-access-policy' && policyFacilities.some((facility) => String(facility._id) === String(selectedFacilityId)),
  });

  const currentLmsCommercial = useMemo(
    () => getServiceEntitlements(billingOverviewData, organization)?.lms || {},
    [billingOverviewData, organization]
  );
  const currentConsultingEntitlements = useMemo(
    () => getServiceEntitlements(billingOverviewData, organization)?.consulting || {},
    [billingOverviewData, organization]
  );
  const currentStaffingEntitlements = useMemo(
    () => getServiceEntitlements(billingOverviewData, organization)?.staffing || {},
    [billingOverviewData, organization]
  );

  useEffect(() => {
    let mounted = true;
    const loadPreferences = async () => {
      setPrefsLoading(true);
      try {
        const res = await fetch('/api/notifications/preferences', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          if (mounted) {
            setNotificationPrefs(json.preferences || {});
            if (json.global) setGlobalPrefs(json.global);
          }
        }
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setPrefsLoading(false);
      }
    };

    loadPreferences();
    return () => { mounted = false; };
  }, [token]);

  useEffect(() => {
    if (!organization) return;
    setServiceRequestForm((prev) => {
      const hasStartedRequest =
        prev.reason ||
        Object.values(prev.requestedServices || {}).some((value) => value !== 'no_change');
      if (hasStartedRequest) return prev;
      const base = buildServiceRequestFormFromOrganization({ ...organization, billingOverview: billingOverviewData });
      if (!isOrgAdminSettings) {
        const entitlements = getServiceEntitlements(billingOverviewData, organization);
        const consultingAppliesTo = entitlements?.consulting?.coverage?.appliesTo;
        const consultingIsOrgOnly = consultingAppliesTo === 'organization' || (!consultingAppliesTo && !entitlements?.consulting?.enabled);
        return {
          ...base,
          consultingRequest: {
            ...base.consultingRequest,
            // Don't prefill org-level service offers for facility users — they are requesting their own independent scope
            serviceOffers: consultingIsOrgOnly ? [{ type: '', categories: [] }] : base.consultingRequest.serviceOffers,
          },
        };
      }
      return base;
    });
  }, [organization, billingOverviewData, isOrgAdminSettings]);

  useEffect(() => {
    if (selectedFacilityId || !primaryScopedFacility?._id) return;
    setSelectedFacilityId(String(primaryScopedFacility._id));
  }, [selectedFacilityId, primaryScopedFacility?._id]);

  useEffect(() => {
    if (isOrgAdminSettings || !primaryScopedFacility?._id) return;
    if (String(selectedFacilityId) !== String(primaryScopedFacility._id)) {
      setSelectedFacilityId(String(primaryScopedFacility._id));
    }
  }, [isOrgAdminSettings, selectedFacilityId, primaryScopedFacility?._id]);

  useEffect(() => {
    if (activeOrgSubtab !== 'internal-access-policy') return;
    if (!policyFacilities.length) return;
    if (!policyFacilities.some((facility) => String(facility._id) === String(selectedFacilityId))) {
      setSelectedFacilityId(String(policyFacilities[0]._id));
    }
  }, [activeOrgSubtab, policyFacilities, selectedFacilityId]);

  useEffect(() => {
    if (selectedOrgFacilityData?.facility) {
      setFacilityInfoDraft(loadFacilityInfoDraft({
        ...selectedOrgFacilityData.facility,
        address: normalizeAddress(selectedOrgFacilityData.facility.address),
      }));
    }
  }, [selectedOrgFacilityData]);

  useEffect(() => {
    if (selectedFacilityData?.facility) {
      setFacilityPolicyDraft(normalizeFacilityPolicy(selectedFacilityData.facility));
    }
  }, [selectedFacilityData]);

  const savePreferences = async () => {
    setPrefsSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/notifications/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ types: notificationPrefs, email: globalPrefs.email, inApp: globalPrefs.inApp }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        if (json.preferences) setNotificationPrefs(json.preferences);
        if (json.global) setGlobalPrefs(json.global);
        setMessage('Preferences saved successfully');
      } else {
        setMessage(json.message || json.error || 'Failed to save preferences');
      }
    } catch (err) {
      setMessage('Failed to save preferences');
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
        })
      });
      if (res.ok) {
        setMessage('Profile updated successfully');
      } else {
        setMessage('Failed to update profile');
      }
    } catch (err) {
      setMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (password.new !== password.confirm) {
      setMessage('Passwords do not match');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: password.current,
          newPassword: password.new
        })
      });
      if (res.ok) {
        setMessage('Password changed successfully');
        setPassword({ current: '', new: '', confirm: '' });
      } else {
        setMessage('Failed to change password');
      }
    } catch (err) {
      setMessage('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // Organization updates (org_admin only)
  const handleOrganizationUpdate = async (e) => {
    e.preventDefault();
    if (isOrgAdminSettings) {
      // organization-level update
      if (!hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'admin') || !organization) return;
      setOrgSaving(true);
      try {
        const allowed = (({ name, website, phone, address, description }) => {
          const out = {
            name,
            website,
            phone,
            address,
            description,
            serviceAccessPolicy: normalizeServiceAccessPolicy(organization.serviceAccessPolicy),
          };

          return out;
        })(organization);

        const res = await fetch(`/api/organizations/${organization._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(allowed),
        });
        if (res.ok) {
          const data = await res.json();
          setOrganization({
            ...data.organization,
            address: normalizeAddress(data.organization?.address),
            services: normalizeServices(data.organization?.services),
            serviceAccessPolicy: normalizeServiceAccessPolicy(data.organization?.serviceAccessPolicy),
          });
          setMessage('Organization updated successfully');
        } else {
          setMessage('Failed to update organization');
        }
      } catch (err) {
        setMessage('Failed to update organization');
      } finally {
        setOrgSaving(false);
      }
    } else {
      // facility-level update
      await saveFacilityInfo();
    }
  };

  const formatRole = (role) => formatRoleLabel(role);
  const policyRoleOptions = [
    { key: 'org_admin', label: 'Headquarters Admin' },
    { key: 'org_team', label: 'Headquarters Team' },
    { key: 'facility_admin', label: 'Facility Admin' },
    { key: 'facility_manager', label: 'Facility Manager' },
  ];
  const facilityBillingRoleOptions = [
    { key: 'org_admin', label: 'Headquarters Admin' },
    { key: 'org_team', label: 'Headquarters Team' },
    { key: 'facility_admin', label: 'Facility Admin' },
    { key: 'facility_manager', label: 'Facility Manager' },
  ];

  const orgSubtabs = isOrgAdminSettings
    ? [
        { key: 'organization-settings', label: orgHeadingLabel },
        { key: 'service-access', label: 'Service Access & Request Service Change' },
        { key: 'internal-access-policy', label: 'Internal Access Policy' },
      ]
    : [
        { key: 'organization-settings', label: orgHeadingLabel },
        { key: 'service-access', label: 'Service Access & Request Service Change' },
        ...(hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')
          ? [{ key: 'internal-access-policy', label: 'Internal Access Policy' }]
          : []),
      ];

  const normalizedPolicy = useMemo(
    () => normalizeServiceAccessPolicy(organization?.serviceAccessPolicy),
    [organization?.serviceAccessPolicy]
  );
  const visibleOrgUsers = useMemo(
    () => orgUsers.filter((u) => ['org_admin', 'org_team', 'facility_admin', 'facility_manager', 'facility_worker'].includes(u.role)),
    [orgUsers]
  );

  const togglePolicyRole = (path, role, checked) => {
    setOrganization((prev) => {
      const currentPolicy = normalizeServiceAccessPolicy(prev?.serviceAccessPolicy);
      const [scope, field] = path.split('.');
      const existing = new Set(currentPolicy?.[scope]?.[field] || []);
      if (checked) existing.add(role);
      else existing.delete(role);
      const next = {
        ...currentPolicy,
        [scope]: {
          ...(currentPolicy?.[scope] || {}),
          [field]: Array.from(existing),
        },
      };
      return { ...prev, serviceAccessPolicy: next };
    });
  };

  const setPolicyUsers = (path, userIds) => {
    setOrganization((prev) => {
      const currentPolicy = normalizeServiceAccessPolicy(prev?.serviceAccessPolicy);
      const [scope, field] = path.split('.');
      const normalized = Array.isArray(userIds) ? Array.from(new Set(userIds.map((id) => String(id)))) : [];
      return {
        ...prev,
        serviceAccessPolicy: {
          ...currentPolicy,
          [scope]: {
            ...(currentPolicy?.[scope] || {}),
            [field]: normalized,
          },
        },
      };
    });
  };

  const setPolicyMode = (scope, field, mode) => {
    setOrganization((prev) => {
      const currentPolicy = normalizeServiceAccessPolicy(prev?.serviceAccessPolicy);
      return {
        ...prev,
        serviceAccessPolicy: {
          ...currentPolicy,
          [scope]: {
            ...(currentPolicy?.[scope] || {}),
            [field]: mode === 'specific_users' ? 'specific_users' : 'role_based',
          },
        },
      };
    });
  };

  const loadServiceRequests = async (orgId) => {
    if (!orgId || !hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view')) return;
    setServiceRequestLoading(true);
    try {
      const res = await fetch(`/api/organizations/${orgId}/service-change-requests?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setServiceRequestHistory(Array.isArray(json.requests) ? json.requests : []);
        if (typeof json.pendingOrgReviewCount === 'number') {
          setPendingOrgReviewCount(json.pendingOrgReviewCount);
        }
      }
    } catch (_err) {
      // ignore
    } finally {
      setServiceRequestLoading(false);
    }
  };

  const loadOrgUsers = async (orgId) => {
    if (
      !orgId ||
      !(
        hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.internal_access_policy, 'view') ||
        hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')
      )
    ) return;
    try {
      const res = await fetch(`/api/organizations/${orgId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      const users = Array.isArray(json.users) ? json.users : [];
      setOrgUsers(users.filter((u) => ['org_admin', 'org_team', 'facility_admin', 'facility_manager', 'facility_worker'].includes(u.role)));
    } catch (_err) {
      // ignore
    }
  };

  const toggleFacilityBillingRole = (field, role, checked) => {
    setFacilityPolicyDraft((prev) => {
      const existing = new Set(prev?.billingAccessPolicy?.[field] || []);
      if (checked) existing.add(role);
      else existing.delete(role);
      return {
        ...prev,
        billingAccessPolicy: {
          ...(prev.billingAccessPolicy || {}),
          [field]: Array.from(existing),
        },
      };
    });
  };

  const setFacilityPolicyMode = (scope, field, mode) => {
    setFacilityPolicyDraft((prev) => ({
      ...prev,
      internalAccessPolicy: {
        ...(prev.internalAccessPolicy || {}),
        [scope]: {
          ...(prev.internalAccessPolicy?.[scope] || {}),
          [field]: mode,
        },
      },
    }));
  };

  const toggleFacilityPolicyRole = (scope, field, role, checked) => {
    setFacilityPolicyDraft((prev) => {
      const existing = new Set(prev?.internalAccessPolicy?.[scope]?.[field] || []);
      if (checked) existing.add(role);
      else existing.delete(role);
      return {
        ...prev,
        internalAccessPolicy: {
          ...(prev.internalAccessPolicy || {}),
          [scope]: {
            ...(prev.internalAccessPolicy?.[scope] || {}),
            [field]: Array.from(existing),
          },
        },
      };
    });
  };

  const setFacilityPolicyUsers = (scope, field, values) => {
    const normalized = Array.isArray(values) ? Array.from(new Set(values.map((value) => String(value)))) : [];
    setFacilityPolicyDraft((prev) => ({
      ...prev,
      internalAccessPolicy: {
        ...(prev.internalAccessPolicy || {}),
        [scope]: {
          ...(prev.internalAccessPolicy?.[scope] || {}),
          [field]: normalized,
        },
      },
    }));
  };

  const setFacilityPolicyValue = (scope, field, value) => {
    setFacilityPolicyDraft((prev) => ({
      ...prev,
      internalAccessPolicy: {
        ...(prev.internalAccessPolicy || {}),
        [scope]: {
          ...(prev.internalAccessPolicy?.[scope] || {}),
          [field]: value,
        },
      },
    }));
  };

  const setFacilityControlAuthority = (area, value) => {
    setOrganization((prev) => ({
      ...prev,
      facilityControlAuthority: {
        ...(prev.facilityControlAuthority || {}),
        [area]: value,
      },
    }));
  };

  const setServiceExpansionPolicy = (field, value) => {
    setOrganization((prev) => ({
      ...prev,
      serviceExpansionPolicy: {
        ...(prev.serviceExpansionPolicy || {}),
        [field]: value,
      },
    }));
  };

  const saveOrgInternalPolicy = async () => {
    if (!canEditOrgInternalPolicy || !organization) return;
    setOrgSaving(true);
    try {
      const res = await fetch(`/api/organizations/${organization._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          serviceAccessPolicy: normalizeServiceAccessPolicy(organization.serviceAccessPolicy),
          facilityControlAuthority: organization.facilityControlAuthority || {},
          serviceExpansionPolicy: organization.serviceExpansionPolicy || {},
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setOrganization((prev) => ({
          ...prev,
          serviceAccessPolicy: normalizeServiceAccessPolicy(data.organization?.serviceAccessPolicy),
          facilityControlAuthority: data.organization?.facilityControlAuthority || prev.facilityControlAuthority,
          serviceExpansionPolicy: data.organization?.serviceExpansionPolicy || prev.serviceExpansionPolicy,
        }));
        setMessage('Internal access policy saved successfully');
      } else {
        setMessage('Failed to save internal access policy');
      }
    } catch (err) {
      setMessage('Failed to save internal access policy');
    } finally {
      setOrgSaving(false);
    }
  };

  const saveFacilityPolicy = async () => {
    if (!selectedFacilityId || !canEditFacilityInternalPolicy) return;
    setSavingFacilityPolicy(true);
    try {
      await staffingAPI.updateFacility(selectedFacilityId, {
        internalAccessPolicy: facilityPolicyDraft.internalAccessPolicy,
        billing: {
          accessPolicy: facilityPolicyDraft.billingAccessPolicy,
        },
      });
      await refetchSelectedFacility();
      setMessage('Facility policy updated successfully');
    } catch (error) {
      setMessage(error.message || 'Failed to update facility policy');
    } finally {
      setSavingFacilityPolicy(false);
    }
  };

  const loadFacilityInfoDraft = (facility) => ({
    name: facility?.name || '',
    website: facility?.website || '',
    phone: facility?.phone || '',
    address: facility?.address || { street: '', city: '', state: '', zip: '' },
    description: facility?.description || '',
    requireTimeApproval: facility?.preferences?.requireTimeApproval ?? true,
    requirements: {
      minExperience: facility?.requirements?.minExperience ?? '',
      credentialsRequired: facility?.requirements?.credentialsRequired ?? null,
      orientation: {
        required: facility?.requirements?.orientation?.required ?? null,
        duration: facility?.requirements?.orientation?.duration ?? '',
        paidOrientation: facility?.requirements?.orientation?.paidOrientation ?? null,
      },
    },
  });

  const saveFacilityInfo = async () => {
    if (!selectedFacilityId) return;
    setSavingFacilityInfo(true);
    try {
      const req = facilityInfoDraft.requirements;
      const requirementsPayload = {
        minExperience: req.minExperience !== '' ? Number(req.minExperience) : null,
        credentialsRequired: req.credentialsRequired,
        orientation: {
          required: req.orientation.required,
          duration: req.orientation.duration !== '' ? Number(req.orientation.duration) : null,
          paidOrientation: req.orientation.paidOrientation,
        },
      };
      const [{ data }, staffingResult] = await Promise.all([
        organizationsAPI.updateFacility(organization?._id, selectedFacilityId, {
          name: facilityInfoDraft.name,
          website: facilityInfoDraft.website,
          phone: facilityInfoDraft.phone,
          address: facilityInfoDraft.address,
          description: facilityInfoDraft.description,
          requirements: requirementsPayload,
        }),
        staffingAPI.updateFacility(selectedFacilityId, {
          preferences: { requireTimeApproval: facilityInfoDraft.requireTimeApproval },
        }),
      ]);
      await refetchOrgFacilities();
      await refetchSelectedOrgFacility();
      const staffingFacility = staffingResult?.data?.facility;
      setFacilityInfoDraft(loadFacilityInfoDraft({ ...data?.facility, preferences: staffingFacility?.preferences }));
      setMessage('Facility information updated successfully');
    } catch (error) {
      setMessage(error.message || 'Failed to update facility info');
    } finally {
      setSavingFacilityInfo(false);
    }
  };

  const createFacility = async () => {
    if (!canCreateFacility || !organization?._id) return;
    const trimmedName = String(newFacilityDraft.name || '').trim();
    if (!trimmedName) {
      setMessage('Facility name is required');
      return;
    }

    setCreatingFacility(true);
    try {
      const { data } = await organizationsAPI.createFacility(organization._id, {
        name: trimmedName,
        type: newFacilityDraft.type || 'other',
        website: newFacilityDraft.website || '',
        phone: newFacilityDraft.phone || '',
        address: newFacilityDraft.address || { street: '', city: '', state: '', zip: '' },
        description: newFacilityDraft.description || '',
      });
      await refetchOrgFacilities();
      const createdFacilityId = data?.facility?._id ? String(data.facility._id) : '';
      if (createdFacilityId) {
        setSelectedFacilityId(createdFacilityId);
      }
      setNewFacilityDraft({
        name: '',
        type: 'other',
        website: '',
        phone: '',
        address: { street: '', city: '', state: '', zip: '' },
        description: '',
      });
      setMessage('Facility created successfully');
    } catch (error) {
      setMessage(error.message || 'Failed to create facility');
    } finally {
      setCreatingFacility(false);
    }
  };

  const setServiceRequestLmsScoping = (field, value) => {
    setServiceRequestForm((prev) => ({
      ...prev,
      lmsScoping: {
        ...prev.lmsScoping,
        [field]: value,
      },
    }));
  };

  const setServiceRequestCustomField = (field, value) => {
    setServiceRequestForm((prev) => ({
      ...prev,
      lmsCustomCourseRequest: {
        ...prev.lmsCustomCourseRequest,
        [field]: value,
      },
    }));
  };

  const setServiceRequestAssetField = (field, value) => {
    setServiceRequestForm((prev) => ({
      ...prev,
      lmsCustomCourseRequest: {
        ...prev.lmsCustomCourseRequest,
        assetsProvided: {
          ...prev.lmsCustomCourseRequest.assetsProvided,
          [field]: value,
        },
      },
    }));
  };

  const toggleServiceRequestLmsAudience = (audience) => {
    setServiceRequestForm((prev) => {
      const current = parseAudienceList(prev.lmsScoping.trainingAudience);
      const selected = current.filter((entry) => lmsAudienceOptions.includes(entry));
      const other = current.filter((entry) => !lmsAudienceOptions.includes(entry)).join(', ');
      const nextSelected = selected.includes(audience)
        ? selected.filter((entry) => entry !== audience)
        : [...selected, audience];
      return {
        ...prev,
        lmsScoping: {
          ...prev.lmsScoping,
          trainingAudience: buildAudienceValue(nextSelected, other),
        },
      };
    });
  };

  const setServiceRequestLmsAudienceOther = (value) => {
    setServiceRequestForm((prev) => {
      const current = parseAudienceList(prev.lmsScoping.trainingAudience);
      const selected = current.filter((entry) => lmsAudienceOptions.includes(entry));
      return {
        ...prev,
        lmsScoping: {
          ...prev.lmsScoping,
          trainingAudience: buildAudienceValue(selected, value),
        },
      };
    });
  };

  const toggleServiceRequestCustomAudience = (audience) => {
    setServiceRequestForm((prev) => {
      const current = Array.isArray(prev.lmsCustomCourseRequest.targetAudience)
        ? prev.lmsCustomCourseRequest.targetAudience
        : [];
      const next = current.includes(audience)
        ? current.filter((entry) => entry !== audience)
        : [...current, audience];
      return {
        ...prev,
        lmsCustomCourseRequest: {
          ...prev.lmsCustomCourseRequest,
          targetAudience: next,
        },
      };
    });
  };

  const setServiceRequestObjective = (index, value) => {
    setServiceRequestForm((prev) => {
      const learningObjectives = Array.isArray(prev.lmsCustomCourseRequest.learningObjectives)
        ? [...prev.lmsCustomCourseRequest.learningObjectives]
        : Array.from({ length: customCourseObjectiveSlots }, () => '');
      learningObjectives[index] = value;
      return {
        ...prev,
        lmsCustomCourseRequest: {
          ...prev.lmsCustomCourseRequest,
          learningObjectives,
        },
      };
    });
  };

  const updateConsultingServiceOffer = (index, patch) => {
    setServiceRequestForm((prev) => {
      const nextOffers = [...(prev.consultingRequest?.serviceOffers || [])];
      nextOffers[index] = { ...(nextOffers[index] || { type: '', categories: [] }), ...patch };
      return {
        ...prev,
        consultingRequest: {
          ...prev.consultingRequest,
          serviceOffers: nextOffers,
        },
      };
    });
  };

  const addConsultingServiceOffer = () => {
    setServiceRequestForm((prev) => ({
      ...prev,
      consultingRequest: {
        ...prev.consultingRequest,
        serviceOffers: [...(prev.consultingRequest?.serviceOffers || []), { type: '', categories: [] }],
      },
    }));
  };

  const removeConsultingServiceOffer = (index) => {
    setServiceRequestForm((prev) => {
      const current = prev.consultingRequest?.serviceOffers || [];
      const nextOffers = current.filter((_, offerIndex) => offerIndex !== index);
      return {
        ...prev,
        consultingRequest: {
          ...prev.consultingRequest,
          serviceOffers: nextOffers.length ? nextOffers : [{ type: '', categories: [] }],
        },
      };
    });
  };

  const toggleConsultingCategory = (offerIndex, category) => {
    setServiceRequestForm((prev) => {
      const nextOffers = [...(prev.consultingRequest?.serviceOffers || [])];
      const currentOffer = nextOffers[offerIndex] || { type: '', categories: [] };
      const currentCategories = new Set(currentOffer.categories || []);
      if (currentCategories.has(category)) {
        currentCategories.delete(category);
      } else {
        currentCategories.add(category);
      }
      nextOffers[offerIndex] = {
        ...currentOffer,
        categories: Array.from(currentCategories),
      };
      return {
        ...prev,
        consultingRequest: {
          ...prev.consultingRequest,
          serviceOffers: nextOffers,
        },
      };
    });
  };

  const toggleServiceRequestFacility = (scopeKey, facilityId) => {
    const normalizedId = String(facilityId || '').trim();
    if (!normalizedId) return;
    setServiceRequestForm((prev) => {
      const currentIds = scopeKey === 'consulting'
        ? (prev.consultingRequest?.facilityIds || [])
        : scopeKey === 'staffing'
          ? (prev.staffingRequest?.facilityIds || [])
          : (prev.lmsScoping?.facilityIds || []);
      const nextIds = currentIds.includes(normalizedId)
        ? currentIds.filter((entry) => entry !== normalizedId)
        : [...currentIds, normalizedId];
      if (scopeKey === 'consulting') {
        return {
          ...prev,
          consultingRequest: {
            ...prev.consultingRequest,
            facilityIds: nextIds,
          },
        };
      }
      if (scopeKey === 'staffing') {
        return {
          ...prev,
          staffingRequest: {
            ...prev.staffingRequest,
            requestScopeType: 'selected_facilities',
            facilityIds: nextIds,
          },
        };
      }
      return {
        ...prev,
        lmsScoping: {
          ...prev.lmsScoping,
          facilityIds: nextIds,
        },
      };
    });
  };

  const setAllServiceRequestFacilities = (scopeKey) => {
    const allIds = serviceRequestFacilities.map((facility) => String(facility?._id || '')).filter(Boolean);
    setServiceRequestForm((prev) => {
      if (scopeKey === 'consulting') {
        return {
          ...prev,
          consultingRequest: {
            ...prev.consultingRequest,
            facilityIds: allIds,
          },
        };
      }
      if (scopeKey === 'staffing') {
        return {
          ...prev,
          staffingRequest: {
            ...prev.staffingRequest,
            requestScopeType: 'all_active_facilities',
            facilityIds: allIds,
          },
        };
      }
      return {
        ...prev,
        lmsScoping: {
          ...prev.lmsScoping,
          facilityIds: allIds,
        },
      };
    });
  };

  const submitServiceChangeRequest = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!organization?._id) return;
    const requestEntries = Object.entries(serviceRequestForm.requestedServices || {})
      .filter(([, action]) => action === 'enable' || action === 'disable');
    const hasSelection = requestEntries.length > 0;
    if (!hasSelection) {
      setMessage('Select at least one service change (enable or disable).');
      return;
    }
    if (!String(serviceRequestForm.reason || '').trim()) {
      setMessage('Please add a reason for the service change request.');
      return;
    }

    if (serviceRequestForm.requestedServices?.consulting === 'enable') {
      const validConsultingOffers = (serviceRequestForm.consultingRequest?.serviceOffers || []).filter(
        (offer) => offer.type && Array.isArray(offer.categories) && offer.categories.length > 0
      );
      if (!validConsultingOffers.length) {
        setMessage('Select at least one consulting service and subcategory for the consulting request.');
        return;
      }
      if (serviceRequestFacilities.length > 0 && !(serviceRequestForm.consultingRequest?.facilityIds || []).length) {
        setMessage('Select at least one facility for the consulting expansion request.');
        return;
      }
    }

    if (serviceRequestForm.requestedServices?.staffing === 'enable') {
      if (
        serviceRequestForm.staffingRequest?.requestScopeType === 'selected_facilities' &&
        orgFacilities.length > 0 &&
        !(serviceRequestForm.staffingRequest?.facilityIds || []).length
      ) {
        setMessage('Select at least one facility for the staffing access request, or switch staffing access to all active facilities.');
        return;
      }
    }

    if (serviceRequestForm.requestedServices?.lms === 'enable') {
      const wantsStandard = Boolean(serviceRequestForm.lmsRequest?.standardAccess);
      const wantsCustom = Boolean(serviceRequestForm.lmsRequest?.customDevelopment);
      if (!wantsStandard && !wantsCustom) {
        setMessage('Select standard LMS access, custom development, or both for the LMS request.');
        return;
      }
      if (wantsCustom && !String(serviceRequestForm.lmsCustomCourseRequest?.courseTitle || '').trim()) {
        setMessage('Add a custom course title before submitting the LMS custom development request.');
        return;
      }
      if (orgFacilities.length > 0 && !(serviceRequestForm.lmsScoping?.facilityIds || []).length) {
        setMessage('Select at least one facility for the LMS expansion request.');
        return;
      }
    }

    const requestedServices = requestEntries.reduce((acc, [serviceKey, action]) => {
      acc[serviceKey] = action === 'enable';
      return acc;
    }, {});

    const wantsLmsEnable = requestedServices.lms === true;
    const wantsStandardLms = wantsLmsEnable && Boolean(serviceRequestForm.lmsRequest?.standardAccess);
    const wantsCustomLms = wantsLmsEnable && Boolean(serviceRequestForm.lmsRequest?.customDevelopment);
    const validConsultingOffers = (serviceRequestForm.consultingRequest?.serviceOffers || []).filter(
      (offer) => offer.type && Array.isArray(offer.categories) && offer.categories.length > 0
    );
    const learningObjectives = Array.isArray(serviceRequestForm.lmsCustomCourseRequest?.learningObjectives)
      ? serviceRequestForm.lmsCustomCourseRequest.learningObjectives.map((item) => String(item || '').trim()).filter(Boolean)
      : [];

    setServiceRequestSaving(true);
    try {
      const res = await fetch(`/api/organizations/${organization._id}/service-change-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          requestedServices,
          consultingRequest: requestedServices.consulting === true
            ? {
              ...serviceRequestForm.consultingRequest,
              facilityIds: serviceRequestForm.consultingRequest?.facilityIds || [],
              serviceOffers: validConsultingOffers,
            }
            : undefined,
          staffingRequest: requestedServices.staffing === true
            ? {
              ...serviceRequestForm.staffingRequest,
              facilityIds: serviceRequestForm.staffingRequest?.facilityIds || [],
            }
            : undefined,
          lmsScoping: wantsLmsEnable && (wantsStandardLms || wantsCustomLms) ? serviceRequestForm.lmsScoping : undefined,
          lmsCustomCourseRequest: wantsCustomLms
            ? {
              ...serviceRequestForm.lmsCustomCourseRequest,
              hostingType: serviceRequestForm.lmsRequest.hostingType,
              learningObjectives,
            }
            : undefined,
          reason: serviceRequestForm.reason,
          submittingFacilityId: user?.role === 'facility_admin' ? selectedFacilityId || undefined : undefined,
          payer: user?.role === 'facility_admin' ? serviceRequestForm.payer || 'facility' : undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.error || 'Failed to submit service change request');
        return;
      }
      setServiceRequestForm(defaultServiceRequestForm());
      setMessage(
        json.pendingOrgReview
          ? 'Request submitted — awaiting your organization administrator\'s approval before it is sent to Stratel.'
          : 'Service change request submitted successfully.'
      );
      await loadServiceRequests(organization._id);
    } catch (_err) {
      setMessage('Failed to submit service change request');
    } finally {
      setServiceRequestSaving(false);
    }
  };

  const handleOrgDecision = async (requestId, decision) => {
    if (!organization?._id) return;
    const note = orgDecisionNote.trim();
    if (decision === 'rejected' && !note) {
      setMessage('Please add a note explaining why the request is not approved.');
      return;
    }
    setOrgDecisionSaving(requestId);
    try {
      const res = await fetch(`/api/organizations/service-change-requests/${requestId}/org-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ decision, note }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.error || 'Failed to process decision');
        return;
      }
      setOrgDecisionNote('');
      setMessage(decision === 'approved'
        ? 'Request approved and forwarded to Stratel.'
        : 'Request declined. The facility has been notified.');
      await loadServiceRequests(organization._id);
    } catch (_err) {
      setMessage('Failed to process decision');
    } finally {
      setOrgDecisionSaving(null);
    }
  };

  // Project status options for the multi-select
  // Load organization for org_admin
  useEffect(() => {
    let mounted = true;
    const loadOrg = async () => {
      if (!(hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') || hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view') || hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')) || !user?.organizationId) return;
      setOrgLoading(true);
      try {
        const res = await fetch(`/api/organizations/${user.organizationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          if (mounted) {
            setOrganization({
              ...json.organization,
              address: normalizeAddress(json.organization?.address),
              services: normalizeServices(json.organization?.services),
              serviceAccessPolicy: normalizeServiceAccessPolicy(json.organization?.serviceAccessPolicy),
            });
            loadServiceRequests(json.organization?._id);
            loadOrgUsers(json.organization?._id);
          }
        } else {
          // ignore silently; UI will show not found
        }
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setOrgLoading(false);
      }
    };

    loadOrg();
    return () => { mounted = false; };
  }, [user?.role, user?.organizationId, token]);

  useEffect(() => {
    let mounted = true;
    const loadSecurity = async () => {
      setSecurityLoading(true);
      try {
        const res = await fetch('/api/auth/security', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!mounted || !res.ok) return;
        setSecurity({
          twoFactor: {
            enabled: Boolean(json.security?.twoFactor?.enabled),
            method: json.security?.twoFactor?.method || 'email',
            hasTotp: Boolean(json.security?.twoFactor?.hasTotp),
            backupCodesAvailable: Number(json.security?.twoFactor?.backupCodesAvailable || 0),
          },
          passkeys: {
            enabled: Boolean(json.security?.passkeys?.enabled),
            count: Number(json.security?.passkeys?.count || 0),
          },
          email: json.security?.email || user?.email || '',
        });
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setSecurityLoading(false);
      }
    };
    loadSecurity();
    return () => { mounted = false; };
  }, [token, user?.email]);

  useEffect(() => {
    if (activeTab !== 'security') return;
    let mounted = true;
    const loadPasskeys = async () => {
      try {
        const res = await fetch('/api/auth/passkeys', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!mounted || !res.ok) return;
        setPasskeys(Array.isArray(json.passkeys) ? json.passkeys : []);
      } catch {
        // ignore
      }
    };
    loadPasskeys();
    return () => { mounted = false; };
  }, [activeTab, token]);

  const requestEnableTwoFactor = async () => {
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/2fa/email/enable-request', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to request verification code');
        return;
      }
      setTwoFactorChallengeId(json.challengeId || '');
      if (json.devCode && import.meta.env.DEV) {
        setMessage(`success: verification code sent to your email. Dev code: ${json.devCode}`);
        return;
      }
      setMessage('success: verification code sent to your email.');
    } catch (err) {
      setMessage('Failed to request verification code');
    } finally {
      setSecurityBusy(false);
    }
  };

  const verifyEnableTwoFactor = async (e) => {
    e.preventDefault();
    if (!twoFactorChallengeId || !twoFactorCode) {
      setMessage('Enter the verification code sent to your email.');
      return;
    }
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/2fa/email/enable-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ challengeId: twoFactorChallengeId, code: twoFactorCode }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to enable 2FA');
        return;
      }
      setSecurity((prev) => ({
        ...prev,
        twoFactor: {
          ...prev.twoFactor,
          enabled: true,
          method: 'email',
          backupCodesAvailable: Array.isArray(json.backupCodes) ? json.backupCodes.length : prev.twoFactor?.backupCodesAvailable,
        },
      }));
      setTwoFactorChallengeId('');
      setTwoFactorCode('');
      if (Array.isArray(json.backupCodes)) setBackupCodes(json.backupCodes);
      setMessage('success: two-factor authentication enabled.');
    } catch (err) {
      setMessage('Failed to enable 2FA');
    } finally {
      setSecurityBusy(false);
    }
  };

  const disableTwoFactor = async (e) => {
    e.preventDefault();
    if (!disableTwoFactorPassword) {
      setMessage('Current password is required to disable 2FA.');
      return;
    }
    setSecurityBusy(true);
    setMessage('');
    try {
      const disablePath = security?.twoFactor?.method === 'totp' ? '/api/auth/2fa/totp/disable' : '/api/auth/2fa/email/disable';
      const res = await fetch(disablePath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: disableTwoFactorPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to disable 2FA');
        return;
      }
      setSecurity((prev) => ({
        ...prev,
        twoFactor: { enabled: false, method: 'email', hasTotp: false, backupCodesAvailable: 0 },
      }));
      setDisableTwoFactorPassword('');
      setTotpSetup(null);
      setTotpSetupCode('');
      setBackupCodes([]);
      setMessage('success: two-factor authentication disabled.');
    } catch (err) {
      setMessage('Failed to disable 2FA');
    } finally {
      setSecurityBusy(false);
    }
  };

  const requestTotpSetup = async () => {
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/2fa/totp/setup', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to initialize authenticator setup');
        return;
      }
      setTotpSetup({
        qrDataUrl: json.qrDataUrl || '',
        manualKey: json.manualKey || '',
      });
      setMessage('success: scan the QR code and enter your authenticator code to finish setup.');
    } catch (err) {
      setMessage('Failed to initialize authenticator setup');
    } finally {
      setSecurityBusy(false);
    }
  };

  const verifyEnableTotp = async (e) => {
    e.preventDefault();
    if (!totpSetupCode) {
      setMessage('Enter the authenticator code to enable app-based 2FA.');
      return;
    }
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/2fa/totp/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: totpSetupCode }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to enable authenticator app');
        return;
      }
      setSecurity((prev) => ({
        ...prev,
        twoFactor: {
          ...prev.twoFactor,
          enabled: true,
          method: 'totp',
          hasTotp: true,
          backupCodesAvailable: Array.isArray(json.backupCodes) ? json.backupCodes.length : 0,
        },
      }));
      setBackupCodes(Array.isArray(json.backupCodes) ? json.backupCodes : []);
      setTotpSetup(null);
      setTotpSetupCode('');
      setTwoFactorChallengeId('');
      setTwoFactorCode('');
      setMessage('success: authenticator app enabled.');
    } catch (err) {
      setMessage('Failed to enable authenticator app');
    } finally {
      setSecurityBusy(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!disableTwoFactorPassword) {
      setMessage('Enter current password, then regenerate backup codes.');
      return;
    }
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/2fa/backup-codes/regenerate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: disableTwoFactorPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to regenerate backup codes');
        return;
      }
      setBackupCodes(Array.isArray(json.backupCodes) ? json.backupCodes : []);
      setSecurity((prev) => ({
        ...prev,
        twoFactor: {
          ...prev.twoFactor,
          backupCodesAvailable: Array.isArray(json.backupCodes) ? json.backupCodes.length : prev.twoFactor?.backupCodesAvailable || 0,
        },
      }));
      setMessage('success: backup codes regenerated. Save them now.');
    } catch (err) {
      setMessage('Failed to regenerate backup codes');
    } finally {
      setSecurityBusy(false);
    }
  };

  const copyBackupCodes = async () => {
    if (!backupCodes.length) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setMessage('success: backup codes copied.');
    } catch (_err) {
      setMessage('Unable to copy backup codes. Copy manually.');
    }
  };

  const printBackupCodes = () => {
    if (!backupCodes.length) return;
    const html = `<html><head><title>Backup Codes</title></head><body><h2>Stratel Backup Codes</h2><pre>${backupCodes.join('\n')}</pre></body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const sendPasswordlessLink = async () => {
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch('/api/auth/passwordless/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: security.email || user?.email || '' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to send passwordless sign-in link');
        return;
      }
      setMessage('success: passwordless sign-in link sent.');
    } catch (err) {
      setMessage('Failed to send passwordless sign-in link');
    } finally {
      setSecurityBusy(false);
    }
  };

  const registerPasskey = async () => {
    if (!window.PublicKeyCredential || !navigator.credentials?.create) {
      setMessage('Passkeys are not supported on this device/browser.');
      return;
    }
    setSecurityBusy(true);
    setMessage('');
    try {
      const optionsRes = await fetch('/api/auth/passkeys/register/options', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const optionsJson = await optionsRes.json().catch(() => ({}));
      if (!optionsRes.ok) {
        setMessage(optionsJson.message || optionsJson.error || 'Unable to start passkey setup');
        return;
      }
      const options = optionsJson.options || {};
      const publicKey = {
        ...options,
        challenge: toBuffer(options.challenge),
        user: options.user ? { ...options.user, id: toBuffer(options.user.id) } : undefined,
        excludeCredentials: (options.excludeCredentials || []).map((item) => ({
          ...item,
          id: toBuffer(item.id),
        })),
      };
      const credential = await navigator.credentials.create({ publicKey });
      if (!credential) throw new Error('Passkey setup was cancelled.');
      const response = credential.response;
      const payload = {
        credential: {
          id: credential.id,
          rawId: toBase64Url(credential.rawId),
          type: credential.type,
          authenticatorAttachment: credential.authenticatorAttachment || null,
          response: {
            clientDataJSON: toBase64Url(response.clientDataJSON),
            attestationObject: toBase64Url(response.attestationObject),
            transports: response.getTransports ? response.getTransports() : [],
          },
          clientExtensionResults: credential.getClientExtensionResults?.() || {},
        },
        nickname: passkeyNickname.trim(),
      };
      const verifyRes = await fetch('/api/auth/passkeys/register/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const verifyJson = await verifyRes.json().catch(() => ({}));
      if (!verifyRes.ok) {
        setMessage(verifyJson.message || verifyJson.error || 'Unable to verify passkey');
        return;
      }
      const listRes = await fetch('/api/auth/passkeys', { headers: { Authorization: `Bearer ${token}` } });
      const listJson = await listRes.json().catch(() => ({}));
      const nextPasskeys = Array.isArray(listJson.passkeys) ? listJson.passkeys : [];
      setPasskeys(nextPasskeys);
      setSecurity((prev) => ({
        ...prev,
        passkeys: { enabled: nextPasskeys.length > 0, count: nextPasskeys.length },
      }));
      setPasskeyNickname('');
      setMessage('success: passkey added.');
    } catch (err) {
      setMessage(err.message || 'Failed to add passkey');
    } finally {
      setSecurityBusy(false);
    }
  };

  const removePasskey = async (credentialID) => {
    if (!credentialID) return;
    setSecurityBusy(true);
    setMessage('');
    try {
      const res = await fetch(`/api/auth/passkeys/${encodeURIComponent(credentialID)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(json.message || json.error || 'Failed to remove passkey');
        return;
      }
      const next = passkeys.filter((pk) => pk.credentialID !== credentialID);
      setPasskeys(next);
      setSecurity((prev) => ({
        ...prev,
        passkeys: { enabled: next.length > 0, count: next.length },
      }));
      setMessage('success: passkey removed.');
    } catch {
      setMessage('Failed to remove passkey');
    } finally {
      setSecurityBusy(false);
    }
  };

  return (
    <div className="p-0 sm:p-2 max-w-4xl mx-auto">
      {activeTab === 'organization' && activeOrgSubtab === 'service-access' && (
        <div className="mb-4">
          <Link to="/invoices" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
            <ArrowLeft size={16} /> Billing & Subscriptions
          </Link>
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account and preferences</p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          message.includes('success') 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message}
          <button onClick={() => setMessage('')} className="float-right">x</button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <div className="flex gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mb-px">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 capitalize whitespace-nowrap flex-shrink-0 ${
                activeTab === tab
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'organization' ? (isOrgAdminSettings ? 'Organization' : 'Facility') : tab}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white border rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Profile Information</h3>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={profile.email}
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <input
                type="text"
                value={formatRole(user?.role)}
                disabled
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="bg-white border rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Change Password</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input
                type="password"
                value={password.current}
                onChange={(e) => setPassword({ ...password, current: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={password.new}
                onChange={(e) => setPassword({ ...password, new: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={password.confirm}
                onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Notification Preferences</h3>
            <button
              type="button"
              className="text-sm text-green-700 hover:text-green-800"
              onClick={() => setShowAdvancedNotifications((v) => !v)}
            >
              {showAdvancedNotifications ? 'Show Basic' : 'Show Advanced'}
            </button>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={globalPrefs.email} onChange={(e) => setGlobalPrefs({ ...globalPrefs, email: e.target.checked })} className="w-4 h-4 text-green-600 rounded" />
                <span className="text-gray-700">Enable email globally</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={globalPrefs.inApp} onChange={(e) => setGlobalPrefs({ ...globalPrefs, inApp: e.target.checked })} className="w-4 h-4 text-green-600 rounded" />
                <span className="text-gray-700">Enable in-app globally</span>
              </label>
            </div>
            {clientNotificationGroups.map((group) => {
              const visibleItems = group.items.filter((item) => showAdvancedNotifications || item.basic);
              if (!visibleItems.length) return null;
              return (
                <div key={group.title}>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">{group.title}</h4>
                  <div className="space-y-2">
                    {visibleItems.map((item) => (
                      <div key={item.key} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] items-center gap-2 border rounded-lg px-3 py-2">
                        <div className="text-sm text-gray-700">{item.label}</div>
                        <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={!!notificationPrefs[item.key]?.inApp}
                            onChange={(e) => updatePref(item.key, 'inApp', e.target.checked)}
                            className="w-4 h-4 text-green-600 rounded"
                          />
                          In-app
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={!!notificationPrefs[item.key]?.email}
                            onChange={(e) => updatePref(item.key, 'email', e.target.checked)}
                            className="w-4 h-4 text-green-600 rounded"
                          />
                          Email
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={savePreferences} disabled={prefsSaving} className="mt-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            {prefsSaving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-white border rounded-2xl p-6 md:p-7 space-y-6 shadow-sm">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Security</h3>
            <p className="text-sm text-gray-600">Configure two-factor authentication and account recovery controls.</p>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-gray-900">Two-Factor Authentication (2FA)</p>
                <div className="mt-1 text-xs">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${
                    securityLoading
                      ? 'border-gray-200 text-gray-500 bg-white'
                      : security.twoFactor.enabled
                      ? 'border-emerald-200 text-emerald-700 bg-emerald-100/60'
                      : 'border-amber-200 text-amber-700 bg-amber-100/60'
                  }`}>
                    {securityLoading ? 'Loading…' : security.twoFactor.enabled ? `Enabled (${security.twoFactor.method || 'email'})` : 'Disabled'}
                  </span>
                </div>
              </div>
              {!security.twoFactor.enabled && twoFactorMethodToEnable === 'email' ? (
                <button
                  type="button"
                  onClick={requestEnableTwoFactor}
                  disabled={securityBusy || securityLoading}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  Send Verification Code
                </button>
              ) : null}
              {!security.twoFactor.enabled && twoFactorMethodToEnable === 'totp' ? (
                <button
                  type="button"
                  onClick={requestTotpSetup}
                  disabled={securityBusy || securityLoading}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  Start Authenticator Setup
                </button>
              ) : null}
            </div>

            {!security.twoFactor.enabled ? (
              <div className="rounded-xl border border-emerald-100 bg-white p-3">
                <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-2">Step 1 - Choose method</p>
                <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="twoFactorMethod"
                    checked={twoFactorMethodToEnable === 'email'}
                    onChange={() => setTwoFactorMethodToEnable('email')}
                  />
                  Email code
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="twoFactorMethod"
                    checked={twoFactorMethodToEnable === 'totp'}
                    onChange={() => setTwoFactorMethodToEnable('totp')}
                  />
                  Authenticator app
                </label>
                </div>
              </div>
            ) : null}

            {!security.twoFactor.enabled && twoFactorMethodToEnable === 'email' && twoFactorChallengeId ? (
              <form onSubmit={verifyEnableTwoFactor} className="rounded-xl border border-emerald-100 bg-white p-3 space-y-2">
                <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700">Step 2 - Verify email code</p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter 6-digit verification code"
                />
                <button
                  type="submit"
                  disabled={securityBusy}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                >
                  {securityBusy ? 'Verifying...' : 'Enable 2FA'}
                </button>
                </div>
              </form>
            ) : null}

            {!security.twoFactor.enabled && twoFactorMethodToEnable === 'totp' && totpSetup ? (
              <form onSubmit={verifyEnableTotp} className="rounded-xl border border-emerald-100 bg-white p-3 space-y-3">
                <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700">Step 2 - Scan and verify authenticator</p>
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  {totpSetup.qrDataUrl ? (
                    <img src={totpSetup.qrDataUrl} alt="TOTP QR" className="w-36 h-36 border rounded" />
                  ) : null}
                  <div className="text-xs text-gray-600 space-y-2">
                    <div>Scan this QR code in your authenticator app.</div>
                    <div>
                      Manual key:
                      <div className="font-mono text-[11px] mt-1 break-all">{totpSetup.manualKey}</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                  <input
                    type="text"
                    value={totpSetupCode}
                    onChange={(e) => setTotpSetupCode(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Enter 6-digit authenticator code"
                  />
                  <button
                    type="submit"
                    disabled={securityBusy}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                  >
                    {securityBusy ? 'Verifying...' : 'Enable Authenticator'}
                  </button>
                </div>
              </form>
            ) : null}

            {security.twoFactor.enabled ? (
              <form onSubmit={disableTwoFactor} className="rounded-xl border border-emerald-100 bg-white p-3 space-y-2">
                <div className="text-xs text-gray-600">
                  Backup codes available: {Number(security.twoFactor.backupCodesAvailable || 0)}
                </div>
                <input
                  type="password"
                  value={disableTwoFactorPassword}
                  onChange={(e) => setDisableTwoFactorPassword(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter current password to disable 2FA"
                />
                <button
                  type="submit"
                  disabled={securityBusy}
                  className="px-3 py-2 border border-red-200 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 text-sm"
                >
                  {securityBusy ? 'Disabling...' : 'Disable 2FA'}
                </button>
                <button
                  type="button"
                  onClick={regenerateBackupCodes}
                  disabled={securityBusy}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                >
                  {securityBusy ? 'Working...' : 'Regenerate Backup Codes'}
                </button>
              </form>
            ) : null}

            {backupCodes.length > 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-sm font-medium text-amber-900">Step 3 - Save backup codes (one-time view)</p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm text-amber-900">
                  {backupCodes.map((code) => (
                    <div key={code}>{code}</div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button type="button" onClick={copyBackupCodes} className="px-3 py-1.5 text-xs border rounded-lg bg-white hover:bg-gray-50">Copy Codes</button>
                  <button type="button" onClick={printBackupCodes} className="px-3 py-1.5 text-xs border rounded-lg bg-white hover:bg-gray-50">Print Codes</button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border p-4 space-y-3">
            <div>
              <p className="font-medium text-gray-900">Passkeys</p>
              <p className="text-xs text-gray-600">
                Register a device passkey for passwordless sign-in. Registered: {Number(security?.passkeys?.count || 0)}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <input
                type="text"
                value={passkeyNickname}
                onChange={(e) => setPasskeyNickname(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Optional device label (e.g., Work MacBook)"
              />
              <button
                type="button"
                onClick={registerPasskey}
                disabled={securityBusy}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {securityBusy ? 'Working...' : 'Add Passkey'}
              </button>
            </div>
            {passkeys.length > 0 ? (
              <div className="space-y-2">
                {passkeys.map((pk) => (
                  <div key={pk.credentialID} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-gray-900">{pk.nickname || pk.deviceType || 'Unnamed device'}</div>
                      <div className="text-xs text-gray-500">
                        Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString() : 'recently'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePasskey(pk.credentialID)}
                      className="px-2.5 py-1.5 border border-red-200 text-red-700 rounded-lg hover:bg-red-50"
                      disabled={securityBusy}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border p-4 space-y-3">
            <div>
              <p className="font-medium text-gray-900">Passwordless Sign-In Link</p>
              <p className="text-xs text-gray-600">Send a one-time sign-in link to your email.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <input type="email" value={security.email || user?.email || ''} disabled className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
              <button
                type="button"
                onClick={sendPasswordlessLink}
                disabled={securityBusy}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {securityBusy ? 'Sending...' : 'Send Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Tab */}
      {activeTab === 'organization' && (hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') || hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view') || hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.facility_internal_access_policy, 'view')) && (
        <div className="bg-white border rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">{orgHeadingLabel}</h3>
          {orgLoading ? (
            <div className="p-8 text-center">Loading organization...</div>
          ) : organization ? (
            <div className="space-y-4">
              <div className="border-b">
                <div className="flex gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mb-px">
                  {orgSubtabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveOrgSubtab(tab.key)}
                      className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap ${
                        activeOrgSubtab === tab.key
                          ? 'border-green-600 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.label}
                      {tab.key === 'service-access' && isOrgAdminSettings && pendingOrgReviewCount > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold">{pendingOrgReviewCount}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleOrganizationUpdate} className="space-y-4">
                {activeOrgSubtab === 'organization-settings' && (
                  <>
                    {canCreateFacility && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900">Facilities</h3>
                            <p className="text-sm text-slate-600">
                              Facilities are now managed from the dedicated Facilities page, including contact details and edits.
                            </p>
                          </div>
                          <Link
                            to="/facilities"
                            className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                          >
                            Open Facilities
                          </Link>
                        </div>
                      </div>
                    )}

                    {isOrgAdminSettings ? (
                      // organization-wide form
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input type="text" value={organization.name || ''} onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                          <input type="url" value={organization.website || ''} onChange={(e) => setOrganization({ ...organization, website: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input type="text" value={organization.phone || ''} onChange={(e) => setOrganization({ ...organization, phone: e.target.value })}
                              className="w-full border rounded-lg px-3 py-2" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                            <input type="text" value={organization.address?.street || ''} onChange={(e) => setOrganization({ ...organization, address: { ...(organization.address || {}), street: e.target.value } })}
                              className="w-full border rounded-lg px-3 py-2" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                            <input type="text" value={organization.address?.city || ''} onChange={(e) => setOrganization({ ...organization, address: { ...(organization.address || {}), city: e.target.value } })}
                              className="w-full border rounded-lg px-3 py-2" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input type="text" value={organization.address?.state || ''} onChange={(e) => setOrganization({ ...organization, address: { ...(organization.address || {}), state: e.target.value } })}
                              className="w-full border rounded-lg px-3 py-2" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                            <input type="text" value={organization.address?.zip || ''} onChange={(e) => setOrganization({ ...organization, address: { ...(organization.address || {}), zip: e.target.value } })}
                              className="w-full border rounded-lg px-3 py-2" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea value={organization.description || ''} onChange={(e) => setOrganization({ ...organization, description: e.target.value })} rows={4}
                            className="w-full border rounded-lg px-3 py-2" />
                        </div>
                      </>
                    ) : (
                      // facility-specific form
                      <>
                        {isOrgAdminSettings && orgFacilities.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Select Facility</label>
                            <select
                              value={selectedFacilityId}
                              onChange={(e) => setSelectedFacilityId(e.target.value)}
                              className="w-full border rounded-lg px-3 py-2"
                            >
                              {orgFacilities.map((f) => (
                                <option key={f._id} value={String(f._id)}>{f.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {!isOrgAdminSettings && selectedFacilityId && (
                          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                            Assigned facility: {facilityInfoDraft.name || primaryScopedFacility?.name || 'Facility'}
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={facilityInfoDraft.name}
                            onChange={(e) => setFacilityInfoDraft({ ...facilityInfoDraft, name: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                          <input
                            type="url"
                            value={facilityInfoDraft.website}
                            onChange={(e) => setFacilityInfoDraft({ ...facilityInfoDraft, website: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                              type="text"
                              value={facilityInfoDraft.phone}
                              onChange={(e) => setFacilityInfoDraft({ ...facilityInfoDraft, phone: e.target.value })}
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                            <input
                              type="text"
                              value={facilityInfoDraft.address.street}
                              onChange={(e) => setFacilityInfoDraft({
                                ...facilityInfoDraft,
                                address: { ...facilityInfoDraft.address, street: e.target.value },
                              })}
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                            <input
                              type="text"
                              value={facilityInfoDraft.address.city}
                              onChange={(e) => setFacilityInfoDraft({
                                ...facilityInfoDraft,
                                address: { ...facilityInfoDraft.address, city: e.target.value },
                              })}
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              value={facilityInfoDraft.address.state}
                              onChange={(e) => setFacilityInfoDraft({
                                ...facilityInfoDraft,
                                address: { ...facilityInfoDraft.address, state: e.target.value },
                              })}
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                            <input
                              type="text"
                              value={facilityInfoDraft.address.zip}
                              onChange={(e) => setFacilityInfoDraft({
                                ...facilityInfoDraft,
                                address: { ...facilityInfoDraft.address, zip: e.target.value },
                              })}
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={facilityInfoDraft.description}
                            onChange={(e) => setFacilityInfoDraft({ ...facilityInfoDraft, description: e.target.value })}
                            rows={4}
                            className="w-full border rounded-lg px-3 py-2"
                          />
                        </div>

                        {/* Staffing Preferences — only shown when staffing is enabled */}
                        {organization?.services?.staffing?.enabled && (
                          <div className="border-t pt-4 mt-2">
                            <h5 className="text-sm font-semibold text-gray-800 mb-1">Staffing Preferences</h5>
                            <p className="text-xs text-gray-500 mb-4">These defaults help Stratel match the right workers for your facility. They apply across all staffing requests and can be overridden per request.</p>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum experience (years)</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="30"
                                  value={facilityInfoDraft.requirements.minExperience}
                                  onChange={(e) => setFacilityInfoDraft({
                                    ...facilityInfoDraft,
                                    requirements: { ...facilityInfoDraft.requirements, minExperience: e.target.value },
                                  })}
                                  placeholder="e.g. 1"
                                  className="w-32 border rounded-lg px-3 py-2 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Do your positions generally require credentials?</label>
                                <div className="flex gap-4">
                                  {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(({ value, label }) => (
                                    <label key={label} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="credentialsRequired"
                                        checked={facilityInfoDraft.requirements.credentialsRequired === value}
                                        onChange={() => setFacilityInfoDraft({
                                          ...facilityInfoDraft,
                                          requirements: { ...facilityInfoDraft.requirements, credentialsRequired: value },
                                        })}
                                      />
                                      {label}
                                    </label>
                                  ))}
                                  {facilityInfoDraft.requirements.credentialsRequired !== null && (
                                    <button type="button" onClick={() => setFacilityInfoDraft({
                                      ...facilityInfoDraft,
                                      requirements: { ...facilityInfoDraft.requirements, credentialsRequired: null },
                                    })} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Specific credentials are captured per role when submitting a request.</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Orientation required?</label>
                                <div className="flex gap-4 mb-3">
                                  {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(({ value, label }) => (
                                    <label key={label} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="orientationRequired"
                                        checked={facilityInfoDraft.requirements.orientation.required === value}
                                        onChange={() => setFacilityInfoDraft({
                                          ...facilityInfoDraft,
                                          requirements: {
                                            ...facilityInfoDraft.requirements,
                                            orientation: { ...facilityInfoDraft.requirements.orientation, required: value },
                                          },
                                        })}
                                      />
                                      {label}
                                    </label>
                                  ))}
                                </div>
                                {facilityInfoDraft.requirements.orientation.required === true && (
                                  <div className="grid grid-cols-2 gap-4 ml-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">Duration (hours)</label>
                                      <input
                                        type="number"
                                        min="0"
                                        value={facilityInfoDraft.requirements.orientation.duration}
                                        onChange={(e) => setFacilityInfoDraft({
                                          ...facilityInfoDraft,
                                          requirements: {
                                            ...facilityInfoDraft.requirements,
                                            orientation: { ...facilityInfoDraft.requirements.orientation, duration: e.target.value },
                                          },
                                        })}
                                        placeholder="e.g. 4"
                                        className="w-24 border rounded-lg px-3 py-2 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">Compensation</label>
                                      <div className="flex gap-3 mt-1">
                                        {[{ value: true, label: 'Paid' }, { value: false, label: 'Unpaid' }].map(({ value, label }) => (
                                          <label key={label} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                            <input
                                              type="radio"
                                              name="paidOrientation"
                                              checked={facilityInfoDraft.requirements.orientation.paidOrientation === value}
                                              onChange={() => setFacilityInfoDraft({
                                                ...facilityInfoDraft,
                                                requirements: {
                                                  ...facilityInfoDraft.requirements,
                                                  orientation: { ...facilityInfoDraft.requirements.orientation, paidOrientation: value },
                                                },
                                              })}
                                            />
                                            {label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="border-t pt-4 mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Require timesheet approval</label>
                          <p className="text-xs text-gray-500 mb-2">When disabled, submitted timesheets are auto-approved without client review.</p>
                          <div className="flex gap-4">
                            {[{ label: 'Yes', value: true }, { label: 'No', value: false }].map(({ label, value }) => (
                              <label key={label} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                  type="radio"
                                  name="requireTimeApproval"
                                  checked={facilityInfoDraft.requireTimeApproval === value}
                                  onChange={() => setFacilityInfoDraft({ ...facilityInfoDraft, requireTimeApproval: value })}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {activeOrgSubtab === 'service-access' && (
                  <>
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Service Access (Organization)</h4>
                      <p className="text-xs text-gray-600 mb-3">Provisioned services are managed by Stratel. Use service change requests below to request updates.</p>
                      {purchasedServiceEntries.length === 0 ? (
                        <div className="text-xs text-gray-600">
                          No services are currently provisioned. Contact Stratel to add services to your plan.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {purchasedServiceEntries.map(([key, label]) => (
                            <div key={key} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={Boolean(organization?.services?.[key]?.enabled)}
                                disabled
                              />
                              <span>{label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        {user?.role === 'facility_admin' ? 'Request a Service Change for Your Facility' : 'Request Service Change'}
                      </h4>
                      {user?.role === 'facility_admin' && (
                        <p className="text-xs text-gray-600 mb-3">
                          Changes you request apply to your facility only. Your organization&apos;s other facilities are not affected.
                        </p>
                      )}
                      {user?.role === 'facility_admin' && !organization?.serviceExpansionPolicy?.facilityCanRequest ? (
                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                          Your organization has not turned on service requests from facilities. Ask your organization administrator to turn them on, or to request the change for you.
                        </div>
                      ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {[
                            { key: 'consulting', label: 'Consulting' },
                            { key: 'staffing', label: 'Staffing' },
                            { key: 'lms', label: 'LMS / Training' },
                          ].map((service) => (
                            <div key={service.key}>
                              <label className="block text-xs font-medium text-gray-700 mb-1">{service.label}</label>
                              <select
                                value={serviceRequestForm.requestedServices?.[service.key] || 'no_change'}
                                onChange={(e) => setServiceRequestForm((prev) => ({
                                  ...prev,
                                  requestedServices: {
                                    ...prev.requestedServices,
                                    [service.key]: e.target.value,
                                  },
                                }))}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="no_change">No change</option>
                                <option value="enable">Enable</option>
                                <option value="disable">Disable</option>
                              </select>
                            </div>
                          ))}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                          <textarea
                            value={serviceRequestForm.reason}
                            onChange={(e) => setServiceRequestForm((prev) => ({ ...prev, reason: e.target.value }))}
                            rows={3}
                            className="w-full border rounded-lg px-3 py-2"
                            placeholder="Explain what service change you need and why."
                          />
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                          <h5 className="text-sm font-semibold text-gray-900">Current Service State</h5>
                          <div className="grid gap-3 lg:grid-cols-3">
                            {(() => {
                              const isFacilitySettingsUser = ['facility_admin', 'facility_manager'].includes(user?.role);
                              const consultingAppliesTo = currentConsultingEntitlements?.coverage?.appliesTo;
                              const consultingIsOrgOnly = consultingAppliesTo === 'organization' || (!consultingAppliesTo && !currentConsultingEntitlements?.enabled);
                              if (isFacilitySettingsUser && consultingIsOrgOnly) return null;
                              return (
                                <div className="rounded-lg border border-green-100 bg-green-50 p-3 text-sm text-gray-700">
                                  <div className="font-medium text-green-900">Consulting</div>
                                  <div>Status: {currentConsultingEntitlements?.enabled ? 'Enabled' : 'Not enabled'}</div>
                                  <div>Commercial scope: {formatCommercialAppliesTo(currentConsultingEntitlements?.coverage)}</div>
                                  <div>Covered facilities: {formatCommercialFacilityNames(currentConsultingEntitlements?.coverage)}</div>
                                  <div>Billing model: {currentConsultingEntitlements?.commercialSnapshot?.billingModel?.replace(/_/g, ' ') || '-'}</div>
                                  <div>Current categories: {(currentConsultingEntitlements?.categories || []).join(', ') || '-'}</div>
                                  <div>Current services: {(currentConsultingEntitlements?.effectiveServices || []).join(', ') || '-'}</div>
                                </div>
                              );
                            })()}
                            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-gray-700">
                              <div className="font-medium text-blue-900">Staffing</div>
                              <div>Status: {currentStaffingEntitlements?.enabled ? 'Enabled' : 'Not enabled'}</div>
                              <div>Commercial model: {currentStaffingEntitlements?.commercialSnapshot?.commercialModel?.replace(/_/g, ' ') || 'On demand'}</div>
                              <div>Applies to: {formatCommercialAppliesTo(currentStaffingEntitlements?.coverage)}</div>
                              <div>Covered facilities: {formatCommercialFacilityNames(currentStaffingEntitlements?.coverage)}</div>
                              <div>Request authority: {currentStaffingEntitlements?.requestAuthority === 'organization' ? 'Organization' : '-'}</div>
                              <div>Approved rate roles: {currentStaffingEntitlements?.approvedRoleCount || 0}</div>
                            </div>
                            <div className="rounded-lg border border-purple-100 bg-purple-50 p-3 text-sm text-gray-700">
                              <div className="font-medium text-purple-900">LMS / Training</div>
                              <div>Status: {currentLmsCommercial?.enabled ? 'Enabled' : 'Not enabled'}</div>
                              <div>Commercial scope: {formatLmsCoverageLabel(currentLmsCommercial?.coverage)}</div>
                              <div>Covered facilities: {formatLmsFacilityNames(currentLmsCommercial?.coverage)}</div>
                              <div>Delivery: {formatLmsDeliveryLabel(currentLmsCommercial?.coverage?.deliveryMode)}</div>
                              <div>Pricing model: {currentLmsCommercial?.subscription?.pricingModel || '-'}</div>
                              <div>Payment schedule: {currentLmsCommercial?.subscription?.paymentSchedule || currentLmsCommercial?.subscription?.seatPaymentSchedule || '-'}</div>
                              <div>Seat pool: {currentLmsCommercial?.usage?.seatsLimit || 'Unlimited'}</div>
                              <div>Seats used: {currentLmsCommercial?.usage?.seatsUsed || 0}</div>
                              <div>Seat activity: {currentLmsCommercial?.seatActivity?.summary || 'Not available'}</div>
                            </div>
                          </div>
                        </div>
                        {serviceRequestForm.requestedServices?.consulting === 'enable' && (
                          <div className="rounded-lg border border-green-200 bg-green-50/40 p-4 space-y-4">
                            <div>
                              <h5 className="text-sm font-semibold text-green-900">Consulting Service Expansion Request</h5>
                              <p className="text-xs text-green-800 mt-1">Select the consulting services and subcategories you want to add so Stratel can route and scope the work correctly.</p>
                            </div>
                            <div className="rounded-lg border border-green-200 bg-white p-4 space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <h6 className="text-sm font-semibold text-green-900">Facilities in scope</h6>
                                  <p className="text-xs text-green-800 mt-1">Consulting pricing is facility-based. Select the facilities this request covers.</p>
                                </div>
                                {serviceRequestFacilities.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setAllServiceRequestFacilities('consulting')}
                                    className="text-xs font-medium text-green-700 hover:text-green-800"
                                  >
                                    Select all
                                  </button>
                                )}
                              </div>
                              {serviceRequestFacilities.length === 0 ? (
                                <div className="text-xs text-gray-600">No facilities are on file for this organization yet. Stratel will confirm facility coverage during review.</div>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {serviceRequestFacilities.map((facility) => (
                                    <label key={facility._id} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(serviceRequestForm.consultingRequest?.facilityIds?.includes(String(facility._id)))}
                                        onChange={() => toggleServiceRequestFacility('consulting', facility._id)}
                                      />
                                      {facility.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="space-y-4">
                              {(serviceRequestForm.consultingRequest?.serviceOffers || []).map((offer, index) => {
                                const availableCategories = consultingServiceCategories[offer.type] || [];
                                return (
                                  <div key={`consulting-offer-${index}`} className="rounded-lg border border-green-200 bg-white p-4 space-y-3">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                      <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Consulting service</label>
                                        <select
                                          value={offer.type}
                                          onChange={(e) => updateConsultingServiceOffer(index, { type: e.target.value, categories: [] })}
                                          className="w-full border rounded-lg px-3 py-2 text-sm"
                                        >
                                          <option value="">Select consulting service</option>
                                          {consultingServiceOptions.map((service) => (
                                            <option key={service.value} value={service.value}>{service.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      {(serviceRequestForm.consultingRequest?.serviceOffers || []).length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => removeConsultingServiceOffer(index)}
                                          className="text-sm text-red-600 hover:text-red-700"
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </div>
                                    {offer.type && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-2">Requested subcategories</label>
                                        <div className="flex flex-wrap gap-2">
                                          {availableCategories.map((category) => (
                                            <label key={`${offer.type}-${category}`} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(offer.categories?.includes(category))}
                                                onChange={() => toggleConsultingCategory(index, category)}
                                              />
                                              {category}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                onClick={addConsultingServiceOffer}
                                className="text-sm font-medium text-green-700 hover:text-green-800"
                              >
                                + Add another consulting service
                              </button>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Consulting summary</label>
                              <textarea
                                value={serviceRequestForm.consultingRequest.summary}
                                onChange={(e) => setServiceRequestForm((prev) => ({
                                  ...prev,
                                  consultingRequest: { ...prev.consultingRequest, summary: e.target.value },
                                }))}
                                rows={3}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                placeholder="Optional: summarize the consulting outcome or support needed."
                              />
                            </div>
                          </div>
                        )}
                        {serviceRequestForm.requestedServices?.staffing === 'enable' && (
                          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-4">
                            <div>
                              <h5 className="text-sm font-semibold text-blue-900">Staffing Access Request</h5>
                              <p className="text-xs text-blue-800 mt-1">
                                Staffing is approved as on-demand access. Choose whether your organization should be able to request staffing across all active facilities or only selected facilities.
                              </p>
                            </div>
                            <div className="rounded-lg border border-blue-200 bg-white p-4 space-y-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Request scope</label>
                                <div className="flex flex-wrap gap-2">
                                  <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                    <input
                                      type="radio"
                                      name="staffing-request-scope"
                                      checked={serviceRequestForm.staffingRequest?.requestScopeType === 'all_active_facilities'}
                                      onChange={() => setServiceRequestForm((prev) => ({
                                        ...prev,
                                        staffingRequest: {
                                          ...prev.staffingRequest,
                                          requestScopeType: 'all_active_facilities',
                                          facilityIds: orgFacilities.map((facility) => String(facility?._id || '')).filter(Boolean),
                                        },
                                      }))}
                                    />
                                    All active facilities
                                  </label>
                                  <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                    <input
                                      type="radio"
                                      name="staffing-request-scope"
                                      checked={serviceRequestForm.staffingRequest?.requestScopeType === 'selected_facilities'}
                                      onChange={() => setServiceRequestForm((prev) => ({
                                        ...prev,
                                        staffingRequest: {
                                          ...prev.staffingRequest,
                                          requestScopeType: 'selected_facilities',
                                          facilityIds: prev.staffingRequest?.facilityIds || [],
                                        },
                                      }))}
                                    />
                                    Selected facilities
                                  </label>
                                  <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                    <input
                                      type="radio"
                                      name="staffing-request-scope"
                                      checked={serviceRequestForm.staffingRequest?.requestScopeType === 'organization'}
                                      onChange={() => setServiceRequestForm((prev) => ({
                                        ...prev,
                                        staffingRequest: {
                                          ...prev.staffingRequest,
                                          requestScopeType: 'organization',
                                          facilityIds: [],
                                        },
                                      }))}
                                    />
                                    Organization review only
                                  </label>
                                </div>
                              </div>

                              {serviceRequestForm.staffingRequest?.requestScopeType === 'selected_facilities' && (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div>
                                      <h6 className="text-sm font-semibold text-blue-900">Facilities in staffing request scope</h6>
                                      <p className="text-xs text-blue-800 mt-1">Choose the facilities that should be able to submit on-demand staffing requests under this commercial approval.</p>
                                    </div>
                                    {serviceRequestFacilities.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => setAllServiceRequestFacilities('staffing')}
                                        className="text-xs font-medium text-blue-700 hover:text-blue-800"
                                      >
                                        Select all
                                      </button>
                                    )}
                                  </div>
                                  {serviceRequestFacilities.length === 0 ? (
                                    <div className="text-xs text-gray-600">No facilities are on file for this organization yet. Stratel will confirm staffing request scope during review.</div>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {serviceRequestFacilities.map((facility) => (
                                        <label key={facility._id} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(serviceRequestForm.staffingRequest?.facilityIds?.includes(String(facility._id)))}
                                            onChange={() => toggleServiceRequestFacility('staffing', facility._id)}
                                          />
                                          {facility.name}
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Request summary</label>
                                <textarea
                                  rows={2}
                                  value={serviceRequestForm.staffingRequest?.summary || ''}
                                  onChange={(e) => setServiceRequestForm((prev) => ({
                                    ...prev,
                                    staffingRequest: {
                                      ...prev.staffingRequest,
                                      summary: e.target.value,
                                    },
                                  }))}
                                  className="w-full border rounded-lg px-3 py-2 text-sm"
                                  placeholder="Optional: describe the staffing access need, approved rate tier context, or facilities expected to request coverage."
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {serviceRequestForm.requestedServices?.lms === 'enable' && (
                          <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-4">
                            <div>
                              <h5 className="text-sm font-semibold text-purple-900">LMS Service Expansion Request</h5>
                              <p className="text-xs text-purple-800 mt-1">Use this request for standard LMS access changes, custom course development, or both.</p>
                            </div>
                            <div className="rounded-lg border border-purple-200 bg-white p-4 space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <h6 className="text-sm font-semibold text-purple-900">Facilities in scope</h6>
                                  <p className="text-xs text-purple-800 mt-1">LMS pricing is facility-based. Select the facilities this LMS request covers.</p>
                                </div>
                                {orgFacilities.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setAllServiceRequestFacilities('lms')}
                                    className="text-xs font-medium text-purple-700 hover:text-purple-800"
                                  >
                                    Select all
                                  </button>
                                )}
                              </div>
                              {orgFacilities.length === 0 ? (
                                <div className="text-xs text-gray-600">No facilities are on file for this organization yet. Stratel will confirm facility coverage during review.</div>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {orgFacilities.map((facility) => (
                                    <label key={facility._id} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(serviceRequestForm.lmsScoping?.facilityIds?.includes(String(facility._id)))}
                                        onChange={() => toggleServiceRequestFacility('lms', facility._id)}
                                      />
                                      {facility.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">What LMS change do you need?</label>
                              <div className="flex flex-wrap gap-2">
                                <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(serviceRequestForm.lmsRequest.standardAccess)}
                                    onChange={(e) => setServiceRequestForm((prev) => ({
                                      ...prev,
                                      lmsRequest: { ...prev.lmsRequest, standardAccess: e.target.checked },
                                    }))}
                                  />
                                  Standard LMS Access
                                </label>
                                <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(serviceRequestForm.lmsRequest.customDevelopment)}
                                    onChange={(e) => setServiceRequestForm((prev) => ({
                                      ...prev,
                                      lmsRequest: { ...prev.lmsRequest, customDevelopment: e.target.checked },
                                    }))}
                                  />
                                  Custom Course Development
                                </label>
                              </div>
                            </div>
                            {serviceRequestForm.lmsRequest.standardAccess && (
                              <div className="rounded-lg border border-purple-200 bg-white p-4 space-y-4">
                                <div>
                                  <h6 className="text-sm font-semibold text-purple-900">Standard LMS Discovery</h6>
                                  <p className="text-xs text-purple-800 mt-1">Capture the discovery details Stratel needs for LMS pricing and packaging.</p>
                                </div>
                                {(() => {
                                  const currentAudienceValues = parseAudienceList(serviceRequestForm.lmsScoping.trainingAudience);
                                  const selectedAudienceOptions = currentAudienceValues.filter((entry) => lmsAudienceOptions.includes(entry));
                                  const otherAudienceValue = currentAudienceValues.filter((entry) => !lmsAudienceOptions.includes(entry)).join(', ');
                                  return (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-2">Target audience</label>
                                      <div className="flex flex-wrap gap-2">
                                        {lmsAudienceOptions.map((audience) => (
                                          <label key={audience} className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-gray-200 text-sm cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={selectedAudienceOptions.includes(audience)}
                                              onChange={() => toggleServiceRequestLmsAudience(audience)}
                                            />
                                            {audience}
                                          </label>
                                        ))}
                                      </div>
                                      <input
                                        type="text"
                                        value={otherAudienceValue}
                                        onChange={(e) => setServiceRequestLmsAudienceOther(e.target.value)}
                                        className="w-full border rounded-lg px-3 py-2 mt-3 text-sm"
                                        placeholder="Other audience"
                                      />
                                    </div>
                                  );
                                })()}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Estimated seats</label>
                                    <input
                                      value={serviceRequestForm.lmsScoping.estimatedSeats}
                                      onChange={(e) => setServiceRequestLmsScoping('estimatedSeats', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                      placeholder="e.g., 25, 40-60, or unknown"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Preferred LMS pricing model</label>
                                    <select
                                      value={serviceRequestForm.lmsScoping.preferredPricingModel}
                                      onChange={(e) => setServiceRequestLmsScoping('preferredPricingModel', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    >
                                      <option value="undecided">Need recommendation</option>
                                      <option value="full_library">Full library access</option>
                                      <option value="per_seat">Per course / per seat</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Preferred payment schedule</label>
                                    <select
                                      value={serviceRequestForm.lmsScoping.preferredPaymentSchedule}
                                      onChange={(e) => setServiceRequestLmsScoping('preferredPaymentSchedule', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    >
                                      <option value="undecided">Undecided</option>
                                      <option value="annual">Annual</option>
                                      <option value="monthly">Monthly</option>
                                    </select>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Training goals</label>
                                  <textarea
                                    rows={2}
                                    value={serviceRequestForm.lmsScoping.trainingGoals}
                                    onChange={(e) => setServiceRequestLmsScoping('trainingGoals', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    placeholder="What should the training improve, reinforce, or solve?"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Required topics / compliance areas</label>
                                  <textarea
                                    rows={2}
                                    value={serviceRequestForm.lmsScoping.requiredTopics}
                                    onChange={(e) => setServiceRequestLmsScoping('requiredTopics', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    placeholder="e.g., abuse prevention, dementia care, onboarding"
                                  />
                                </div>
                                <label className="flex items-start gap-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(serviceRequestForm.lmsScoping.needRecommendation)}
                                    onChange={(e) => setServiceRequestLmsScoping('needRecommendation', e.target.checked)}
                                    className="mt-1"
                                  />
                                  <span>
                                    <span className="block font-medium text-gray-900">Need Stratel recommendation</span>
                                    <span className="block text-xs text-gray-500">Check this if you want Stratel to recommend the right LMS package or mix.</span>
                                  </span>
                                </label>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Known courses or topics</label>
                                  <textarea
                                    rows={2}
                                    value={serviceRequestForm.lmsScoping.knownCoursesOrTopics}
                                    onChange={(e) => setServiceRequestLmsScoping('knownCoursesOrTopics', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    placeholder="Optional: any known course titles, certifications, or required topics."
                                  />
                                </div>
                              </div>
                            )}
                            {serviceRequestForm.lmsRequest.customDevelopment && (
                              <div className="rounded-lg border border-teal-200 bg-white p-4 space-y-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <h6 className="text-sm font-semibold text-teal-900">Custom Course Development Request</h6>
                                    <p className="text-xs text-teal-800 mt-1">This is the pre-CCDA scoping request for an existing client expansion.</p>
                                  </div>
                                  <select
                                    value={serviceRequestForm.lmsRequest.hostingType}
                                    onChange={(e) => setServiceRequestForm((prev) => ({
                                      ...prev,
                                      lmsRequest: { ...prev.lmsRequest, hostingType: e.target.value },
                                    }))}
                                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                                  >
                                    <option value="stratel">Hosted on Stratel LMS</option>
                                    <option value="external">External / SCORM delivery</option>
                                  </select>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Request type</label>
                                    <select
                                      value={serviceRequestForm.lmsCustomCourseRequest.requestType}
                                      onChange={(e) => setServiceRequestCustomField('requestType', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    >
                                      {customCourseRequestTypes.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Current LMS subscription</label>
                                    <input
                                      value={serviceRequestForm.lmsCustomCourseRequest.currentSubscriptionType}
                                      onChange={(e) => setServiceRequestCustomField('currentSubscriptionType', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                      placeholder="Annual, monthly, per-seat, not yet subscribed"
                                    />
                                  </div>
                                  <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Course title</label>
                                    <input
                                      value={serviceRequestForm.lmsCustomCourseRequest.courseTitle}
                                      onChange={(e) => setServiceRequestCustomField('courseTitle', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-2">Target audience</label>
                                  <div className="flex flex-wrap gap-2">
                                    {lmsAudienceOptions.map((audience) => (
                                      <label key={audience} className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-gray-200 text-sm cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(serviceRequestForm.lmsCustomCourseRequest.targetAudience.includes(audience))}
                                          onChange={() => toggleServiceRequestCustomAudience(audience)}
                                        />
                                        {audience}
                                      </label>
                                    ))}
                                  </div>
                                  <input
                                    value={serviceRequestForm.lmsCustomCourseRequest.targetAudienceOther}
                                    onChange={(e) => setServiceRequestCustomField('targetAudienceOther', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 mt-3 text-sm"
                                    placeholder="Other audience"
                                  />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Estimated seats</label>
                                    <input
                                      value={serviceRequestForm.lmsCustomCourseRequest.estimatedLearners}
                                      onChange={(e) => setServiceRequestCustomField('estimatedLearners', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Target course length</label>
                                    <select
                                      value={serviceRequestForm.lmsCustomCourseRequest.targetCourseLength}
                                      onChange={(e) => setServiceRequestCustomField('targetCourseLength', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    >
                                      {customCourseLengthOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Preferred delivery date</label>
                                    <input
                                      type="date"
                                      value={serviceRequestForm.lmsCustomCourseRequest.preferredDeliveryDate}
                                      onChange={(e) => setServiceRequestCustomField('preferredDeliveryDate', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(serviceRequestForm.lmsCustomCourseRequest.rushRequired)}
                                      onChange={(e) => setServiceRequestCustomField('rushRequired', e.target.checked)}
                                    />
                                    Rush required
                                  </label>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Regulatory driver</label>
                                    <textarea
                                      rows={2}
                                      value={serviceRequestForm.lmsCustomCourseRequest.regulatoryDriver}
                                      onChange={(e) => setServiceRequestCustomField('regulatoryDriver', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Site-specific regulation / policy</label>
                                    <textarea
                                      rows={2}
                                      value={serviceRequestForm.lmsCustomCourseRequest.siteSpecificPolicy}
                                      onChange={(e) => setServiceRequestCustomField('siteSpecificPolicy', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-2">Learning objectives</label>
                                  <div className="space-y-2">
                                    {Array.from({ length: customCourseObjectiveSlots }, (_, index) => (
                                      <input
                                        key={index}
                                        value={serviceRequestForm.lmsCustomCourseRequest.learningObjectives[index] || ''}
                                        onChange={(e) => setServiceRequestObjective(index, e.target.value)}
                                        className="w-full border rounded-lg px-3 py-2 text-sm"
                                        placeholder={`Objective ${index + 1}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-700 mb-2">Content assets available</p>
                                  <div className="grid gap-2 sm:grid-cols-2 text-sm text-gray-700">
                                    {[
                                      ['existingPolicyDocs', 'Existing policy / procedure documents available'],
                                      ['trainingMaterialsAvailable', 'Existing PowerPoints or training materials available'],
                                      ['subjectMatterExpertAvailable', 'Subject matter expert available'],
                                      ['videoAvailable', 'Video footage available'],
                                      ['brandingAssetsAvailable', 'Facility logo / branding assets available'],
                                      ['complianceScenariosIncluded', 'Specific compliance scenarios to include'],
                                    ].map(([key, label]) => (
                                      <label key={key} className="inline-flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(serviceRequestForm.lmsCustomCourseRequest.assetsProvided[key])}
                                          onChange={(e) => setServiceRequestAssetField(key, e.target.checked)}
                                        />
                                        {label}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">SME name</label>
                                    <input
                                      value={serviceRequestForm.lmsCustomCourseRequest.assetsProvided.subjectMatterExpertName}
                                      onChange={(e) => setServiceRequestAssetField('subjectMatterExpertName', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Branding asset notes</label>
                                    <input
                                      value={serviceRequestForm.lmsCustomCourseRequest.assetsProvided.brandingAssetNotes}
                                      onChange={(e) => setServiceRequestAssetField('brandingAssetNotes', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Compliance scenario notes</label>
                                    <textarea
                                      rows={2}
                                      value={serviceRequestForm.lmsCustomCourseRequest.assetsProvided.complianceScenarioNotes}
                                      onChange={(e) => setServiceRequestAssetField('complianceScenarioNotes', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Additional notes</label>
                                    <textarea
                                      rows={3}
                                      value={serviceRequestForm.lmsCustomCourseRequest.additionalNotes}
                                      onChange={(e) => setServiceRequestCustomField('additionalNotes', e.target.value)}
                                      className="w-full border rounded-lg px-3 py-2 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Who pays, and whether the organization admin approves first */}
                        {user?.role === 'facility_admin' && (
                          <fieldset className="rounded-lg border border-gray-200 bg-white p-3">
                            <legend className="px-1 text-sm font-medium text-gray-700">Who pays for this change?</legend>
                            {[
                              { value: 'facility', label: 'My facility', hint: 'Invoices for this service go to your facility.' },
                              { value: 'organization', label: 'The organization', hint: 'Your organization administrator must approve first.' },
                            ].map((option) => (
                              <label key={option.value} className="flex items-start gap-2 py-1 text-sm text-gray-700">
                                <input
                                  type="radio"
                                  name="service-request-payer"
                                  value={option.value}
                                  checked={(serviceRequestForm.payer || 'facility') === option.value}
                                  onChange={() => setServiceRequestForm((prev) => ({ ...prev, payer: option.value }))}
                                  className="mt-1"
                                />
                                <span>
                                  {option.label}
                                  <span className="block text-xs text-gray-500">{option.hint}</span>
                                </span>
                              </label>
                            ))}
                          </fieldset>
                        )}
                        {user?.role === 'facility_admin' && (
                          (serviceRequestForm.payer === 'organization' || organization?.serviceExpansionPolicy?.requiresOrgApproval) ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                              {serviceRequestForm.payer === 'organization'
                                ? 'Because the organization would pay, your organization administrator reviews this request before it goes to Stratel.'
                                : 'Your organization requires administrator approval before facility requests go to Stratel.'}
                              {' '}You will be notified once they review it.
                            </div>
                          ) : (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                              This request goes straight to Stratel. If you turn off a service your organization added, your organization administrator is told.
                            </div>
                          )
                        )}
                        <button type="button" onClick={submitServiceChangeRequest} disabled={serviceRequestSaving} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
                          {serviceRequestSaving ? 'Submitting...' : 'Submit Service Change Request'}
                        </button>
                      </div>
                      )}

                      {/* Pending facility requests — org admin only */}
                      {isOrgAdminSettings && (() => {
                        const pendingFacilityReqs = serviceRequestHistory.filter((r) => r.status === 'pending_org_review');
                        if (!pendingFacilityReqs.length) return null;
                        return (
                          <div className="mt-6 border border-amber-200 rounded-lg p-4 bg-amber-50">
                            <h5 className="text-sm font-semibold text-amber-900 mb-1">
                              Facility Requests Pending Your Review
                              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-bold">{pendingFacilityReqs.length}</span>
                            </h5>
                            <p className="text-xs text-amber-700 mb-3">These requests were submitted by a facility. Approve to forward to Stratel, or decline with a note.</p>
                            <div className="space-y-4">
                              {pendingFacilityReqs.map((req) => (
                                <div key={req._id} className="bg-white border border-amber-200 rounded-lg p-3">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                      <p className="text-xs font-semibold text-gray-800">
                                        {req.requestedBy?.firstName} {req.requestedBy?.lastName}
                                        <span className="ml-1 text-gray-500 font-normal">({req.requestedBy?.role?.replace(/_/g, ' ')})</span>
                                      </p>
                                      <p className="text-xs text-gray-500">{new Date(req.createdAt).toLocaleDateString()}</p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Pending review</span>
                                  </div>
                                  <div className="text-xs text-gray-700 mb-1">
                                    <span className="font-medium">Services: </span>
                                    {['consulting', 'staffing', 'lms'].filter((k) => req.requestedServices?.[k] !== undefined).map((k) => `${k}: ${req.requestedServices[k] ? 'enable' : 'disable'}`).join(', ') || '—'}
                                  </div>
                                  <div className="text-xs text-gray-600 mb-3 italic">&ldquo;{req.reason}&rdquo;</div>
                                  <textarea
                                    placeholder="Optional note (required if declining)..."
                                    value={orgDecisionSaving === req._id ? orgDecisionNote : (orgDecisionNote && orgDecisionSaving === null ? orgDecisionNote : '')}
                                    onChange={(e) => setOrgDecisionNote(e.target.value)}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2 text-xs mb-2 resize-none"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleOrgDecision(req._id, 'approved')}
                                      disabled={orgDecisionSaving === req._id}
                                      className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                    >
                                      {orgDecisionSaving === req._id ? 'Saving...' : 'Approve & Forward to Stratel'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleOrgDecision(req._id, 'rejected')}
                                      disabled={orgDecisionSaving === req._id}
                                      className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                                    >
                                      Decline
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="mt-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">Recent Requests</h5>
                        {serviceRequestLoading ? (
                          <div className="text-xs text-gray-500">Loading requests...</div>
                        ) : serviceRequestHistory.length === 0 ? (
                          <div className="text-xs text-gray-500">No service change requests yet.</div>
                        ) : (
                          <div className="space-y-2">
                            {serviceRequestHistory.slice(0, 5).map((reqItem) => (
                              <div key={reqItem._id} className="text-xs border rounded-lg p-2 bg-white">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-gray-800">{new Date(reqItem.createdAt).toLocaleDateString()}</span>
                                  <span className={`uppercase tracking-wide text-[10px] rounded-full px-2 py-0.5 font-medium ${
                                    reqItem.status === 'pending_org_review' ? 'bg-amber-100 text-amber-700' :
                                    reqItem.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                                    reqItem.status === 'approved' ? 'bg-green-100 text-green-700' :
                                    reqItem.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {reqItem.status === 'pending_org_review' ? 'Awaiting org approval' : reqItem.status}
                                  </span>
                                </div>
                                {reqItem.orgDecision?.decision && (
                                  <div className={`mb-1 rounded px-2 py-1 ${reqItem.orgDecision.decision === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    Org {reqItem.orgDecision.decision === 'approved' ? 'approved' : 'declined'}
                                    {reqItem.orgDecision.note ? `: ${reqItem.orgDecision.note}` : ''}
                                  </div>
                                )}
                                <div className="text-gray-700 mb-1">
                                  Services: {['consulting', 'staffing', 'lms'].filter((k) => reqItem?.requestedServices?.[k] !== undefined).map((k) => `${k}:${reqItem.requestedServices[k] ? 'on' : 'off'}`).join(', ')}
                                </div>
                                <div className="text-gray-600">{reqItem.reason}</div>
                                {reqItem?.requestedServices?.consulting !== undefined && reqItem?.consultingRequest?.serviceOffers?.length > 0 && (
                                  <div className="mt-2 rounded border border-green-200 bg-green-50 px-2 py-2 text-gray-700">
                                    <div className="font-medium text-green-900 mb-1">Consulting request</div>
                                    {reqItem.consultingRequest.facilityNames?.length > 0 && (
                                      <div>Facilities: {reqItem.consultingRequest.facilityNames.join(', ')}</div>
                                    )}
                                    {reqItem.consultingRequest.serviceOffers.map((offer, index) => (
                                      <div key={`${offer.type || 'consulting'}-${index}`}>
                                        <span className="font-medium">{offer.type || '-'}</span>: {(offer.categories || []).join(', ') || '-'}
                                      </div>
                                    ))}
                                    {reqItem.consultingRequest.summary && <div className="mt-1">Summary: {reqItem.consultingRequest.summary}</div>}
                                  </div>
                                )}
                                {reqItem?.requestedServices?.staffing !== undefined && reqItem?.staffingRequest && (
                                  <div className="mt-2 rounded border border-blue-200 bg-blue-50 px-2 py-2 text-gray-700">
                                    <div className="font-medium text-blue-900 mb-1">Staffing request</div>
                                    <div>Request scope: {formatCommercialAppliesTo({
                                      appliesTo: reqItem.staffingRequest.requestScopeType,
                                      includedFacilityCount: reqItem.staffingRequest.facilityNames?.length || 0,
                                    })}</div>
                                    {reqItem.staffingRequest.facilityNames?.length > 0 && (
                                      <div>Facilities: {reqItem.staffingRequest.facilityNames.join(', ')}</div>
                                    )}
                                    {reqItem.staffingRequest.summary && <div className="mt-1">Summary: {reqItem.staffingRequest.summary}</div>}
                                  </div>
                                )}
                                {reqItem?.requestedServices?.lms !== undefined && reqItem?.lmsScoping && (
                                  <div className="mt-2 rounded border border-purple-200 bg-purple-50 px-2 py-2 text-gray-700">
                                    <div className="font-medium text-purple-900 mb-1">LMS discovery</div>
                                    {reqItem.lmsScoping.facilityNames?.length > 0 && (
                                      <div>Facilities: {reqItem.lmsScoping.facilityNames.join(', ')}</div>
                                    )}
                                    <div>Audience: {reqItem.lmsScoping.trainingAudience || '-'}</div>
                                    <div>Estimated seats: {reqItem.lmsScoping.estimatedSeats || '-'}</div>
                                    <div>Need recommendation: {reqItem.lmsScoping.needRecommendation ? 'Yes' : 'No'}</div>
                                    <div>Pricing model: {reqItem.lmsScoping.preferredPricingModel || '-'}</div>
                                    <div>Payment schedule: {reqItem.lmsScoping.preferredPaymentSchedule || '-'}</div>
                                    {reqItem.lmsScoping.requiredTopics && <div>Topics: {reqItem.lmsScoping.requiredTopics}</div>}
                                  </div>
                                )}
                                {reqItem?.lmsCustomCourseRequest && (
                                  <div className="mt-2 rounded border border-teal-200 bg-teal-50 px-2 py-2 text-gray-700">
                                    <div className="font-medium text-teal-900 mb-1">Custom course request</div>
                                    <div>Type: {reqItem.lmsCustomCourseRequest.requestType || '-'}</div>
                                    <div>Course: {reqItem.lmsCustomCourseRequest.courseTitle || '-'}</div>
                                    <div>Estimated seats: {reqItem.lmsCustomCourseRequest.estimatedLearners || '-'}</div>
                                    <div>Length: {reqItem.lmsCustomCourseRequest.targetCourseLength || '-'}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {activeOrgSubtab === 'internal-access-policy' && (
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{isOrgAdminSettings ? 'Internal Access Policy' : 'Internal Access Policy'}</h4>
                    {!canEditOrgInternalPolicy && isOrgAdminSettings && (
                      <p className="text-xs text-gray-500 mb-3 bg-gray-50 rounded p-2 border">
                        Organization-level policy is managed by your org admin.
                      </p>
                    )}
                    {/* ── reusable inline helpers ── */}
                    {(() => {
                      const fca = organization?.facilityControlAuthority || {};
                      const sep = organization?.serviceExpansionPolicy || {};
                      const pol = normalizedPolicy;

                      // Renders a mode selector + role checkboxes or user picker
                      const PolicyRow = ({ label, modeValue, onModeChange, roles, onToggleRole, userIds, onSetUsers, disabled, modeOptions = [{ value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }], roleOptions = policyRoleOptions }) => (
                        <div className="py-3 border-b border-gray-100 last:border-0">
                          <div className="flex items-center justify-between gap-4 mb-2">
                            <p className="text-sm font-medium text-gray-800">{label}</p>
                            <select value={modeValue} onChange={(e) => onModeChange(e.target.value)} disabled={disabled} className="border rounded-lg px-3 py-1.5 text-sm">
                              {modeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          {modeValue === 'specific_users' ? (
                            <div>
                              <select multiple value={userIds || []} onChange={(e) => onSetUsers(Array.from(e.target.selectedOptions).map((o) => o.value))} disabled={disabled} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[100px]">
                                {visibleOrgUsers.map((u) => <option key={u._id} value={String(u._id)}>{u.firstName} {u.lastName} — {u.email}</option>)}
                              </select>
                              <p className="text-xs text-gray-400 mt-1">Hold Ctrl / Cmd to select multiple.</p>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-3">
                              {roleOptions.map((r) => (
                                <label key={r.key} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={(roles || []).includes(r.key)} onChange={(e) => onToggleRole(r.key, e.target.checked)} disabled={disabled} />
                                  {r.label}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );

                      // Control authority toggle (Org vs Facility)
                      const AuthorityToggle = ({ area, label }) => (
                        <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                          <p className="text-sm text-gray-800">{label}</p>
                          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                            {['organization', 'facility'].map((v) => (
                              <button key={v} type="button" onClick={() => canEditOrgInternalPolicy && setFacilityControlAuthority(area, v)}
                                className={`px-3 py-1.5 ${fca[area] === v || (!fca[area] && v === 'organization') ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} ${!canEditOrgInternalPolicy ? 'cursor-default' : ''}`}>
                                {v === 'organization' ? 'Org Controlled' : 'Facility Controlled'}
                              </button>
                            ))}
                          </div>
                        </div>
                      );

                      return (
                        <div className="space-y-6">

                          {/* ══ ORG ADMIN VIEW ══ */}
                          {isOrgAdminSettings && (
                            <>
                              {/* SECTION 1 — ORG USER ACCESS */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Section 1 — Org User Access</h5>
                                <p className="text-xs text-gray-500 mb-3">Define what organization-level users are allowed to do. Applies to Headquarters Admin and Headquarters Team.</p>

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Training</p>
                                  <PolicyRow label="Can assign LMS courses" modeValue={pol?.lms?.assignmentMode || 'role_based'} onModeChange={(v) => setPolicyMode('lms', 'assignmentMode', v)} roles={pol?.lms?.assignmentRoles} onToggleRole={(r, c) => togglePolicyRole('lms.assignmentRoles', r, c)} userIds={pol?.lms?.assignmentUserIds} onSetUsers={(ids) => setPolicyUsers('lms.assignmentUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Staffing</p>
                                  <PolicyRow label="Can submit staffing requests" modeValue={pol?.staffing?.requesterMode || 'role_based'} onModeChange={(v) => setPolicyMode('staffing', 'requesterMode', v)} roles={pol?.staffing?.requesterRoles} onToggleRole={(r, c) => togglePolicyRole('staffing.requesterRoles', r, c)} userIds={pol?.staffing?.requesterUserIds} onSetUsers={(ids) => setPolicyUsers('staffing.requesterUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Consulting</p>
                                  <PolicyRow label="Can manage consulting collaborators" modeValue={pol?.consulting?.collaboratorMode || 'role_based'} onModeChange={(v) => setPolicyMode('consulting', 'collaboratorMode', v)} roles={pol?.consulting?.collaboratorRoles} onToggleRole={(r, c) => togglePolicyRole('consulting.collaboratorRoles', r, c)} userIds={pol?.consulting?.collaboratorUserIds} onSetUsers={(ids) => setPolicyUsers('consulting.collaboratorUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Billing</p>
                                  <PolicyRow label="Can access organization billing" modeValue={pol?.billing?.accessMode || 'role_based'} onModeChange={(v) => setPolicyMode('billing', 'accessMode', v)} roles={pol?.billing?.accessRoles} onToggleRole={(r, c) => togglePolicyRole('billing.accessRoles', r, c)} userIds={pol?.billing?.accessUserIds} onSetUsers={(ids) => setPolicyUsers('billing.accessUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                  <PolicyRow label="Can manage organization billing actions" modeValue={pol?.billing?.actionMode || 'role_based'} onModeChange={(v) => setPolicyMode('billing', 'actionMode', v)} roles={pol?.billing?.actionRoles} onToggleRole={(r, c) => togglePolicyRole('billing.actionRoles', r, c)} userIds={pol?.billing?.actionUserIds} onSetUsers={(ids) => setPolicyUsers('billing.actionUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Service Requests</p>
                                  <PolicyRow label="Can review facility service expansion/change requests" modeValue={pol?.serviceRequests?.reviewFacilityMode || 'role_based'} onModeChange={(v) => setPolicyMode('serviceRequests', 'reviewFacilityMode', v)} roles={pol?.serviceRequests?.reviewFacilityRoles} onToggleRole={(r, c) => togglePolicyRole('serviceRequests.reviewFacilityRoles', r, c)} userIds={pol?.serviceRequests?.reviewFacilityUserIds} onSetUsers={(ids) => setPolicyUsers('serviceRequests.reviewFacilityUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                  <PolicyRow label="Can approve facility service expansion/change requests" modeValue={pol?.serviceRequests?.approveFacilityMode || 'role_based'} onModeChange={(v) => setPolicyMode('serviceRequests', 'approveFacilityMode', v)} roles={pol?.serviceRequests?.approveFacilityRoles} onToggleRole={(r, c) => togglePolicyRole('serviceRequests.approveFacilityRoles', r, c)} userIds={pol?.serviceRequests?.approveFacilityUserIds} onSetUsers={(ids) => setPolicyUsers('serviceRequests.approveFacilityUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                  <PolicyRow label="Can submit org-level service changes directly to Stratel" modeValue={pol?.serviceRequests?.submitOrgMode || 'role_based'} onModeChange={(v) => setPolicyMode('serviceRequests', 'submitOrgMode', v)} roles={pol?.serviceRequests?.submitOrgRoles} onToggleRole={(r, c) => togglePolicyRole('serviceRequests.submitOrgRoles', r, c)} userIds={pol?.serviceRequests?.submitOrgUserIds} onSetUsers={(ids) => setPolicyUsers('serviceRequests.submitOrgUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>

                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rate Approvals</p>
                                  <PolicyRow label="Can manage rate approvals" modeValue={pol?.rateApprovals?.manageMode || 'role_based'} onModeChange={(v) => setPolicyMode('rateApprovals', 'manageMode', v)} roles={pol?.rateApprovals?.manageRoles} onToggleRole={(r, c) => togglePolicyRole('rateApprovals.manageRoles', r, c)} userIds={pol?.rateApprovals?.manageUserIds} onSetUsers={(ids) => setPolicyUsers('rateApprovals.manageUserIds', ids)} disabled={!canEditOrgInternalPolicy} roleOptions={[{ key: 'org_admin', label: 'Headquarters Admin' }, { key: 'org_team', label: 'Headquarters Team' }]} />
                                </div>
                              </div>

                              {/* SECTION 2 — FACILITY CONTROL AUTHORITY */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Section 2 — Facility Control Authority</h5>
                                <p className="text-xs text-gray-500 mb-3">Choose which service areas facilities can manage their own user access for. When set to Facility Controlled, the facility admin can assign local access for that area.</p>
                                <AuthorityToggle area="lms" label="Training — LMS course assignment access" />
                                <AuthorityToggle area="staffing" label="Staffing — Staffing request submitter access" />
                                <AuthorityToggle area="consulting" label="Consulting — Consulting collaborator management access" />
                              </div>

                              {/* SECTION 3 — WORKER MATCH REJECTION CAP */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Section 3 — Worker Match Rejection Cap</h5>
                                <p className="text-xs text-gray-500 mb-3">View only. Set exclusively by Stratel. Contact Stratel support to change this cap.</p>
                                <div className="flex items-center gap-4">
                                  <div className="w-24 border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 text-center font-semibold">
                                    {Number(pol?.staffing?.matchRejectionCap ?? 3)}
                                  </div>
                                  <p className="text-xs text-gray-500">rejections per requested position before escalation</p>
                                </div>
                              </div>

                              {/* SECTION 4 — BILLING RULES */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Section 4 — Facility Billing Rules</h5>
                                <p className="text-xs text-gray-500 mb-3">Enable or disable what facilities are allowed to see and do regarding billing. Facility admins manage who specifically has access for each enabled permission.</p>
                                {[
                                  { label: 'Facility can view invoices when facility is the billing party', key: 'viewInvoicesFacilityPartyEnabled' },
                                  { label: 'Facility can view invoices when organization is the billing party', key: 'viewInvoicesOrgPartyEnabled' },
                                  { label: 'Facility can view payment status when organization is the billing party', key: 'viewPaymentStatusOrgPartyEnabled' },
                                  { label: 'Facility can pay invoices when facility is the billing party', key: 'payInvoicesFacilityPartyEnabled' },
                                ].map(({ label: bl, key }) => (
                                  <label key={key} className="flex items-center gap-3 text-sm text-gray-700 py-2.5 border-b border-gray-100 last:border-0">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(pol?.billingRules?.[key])}
                                      onChange={(e) => {
                                        if (!canEditOrgInternalPolicy) return;
                                        setOrganization((prev) => {
                                          const currentPolicy = normalizeServiceAccessPolicy(prev?.serviceAccessPolicy);
                                          return {
                                            ...prev,
                                            serviceAccessPolicy: {
                                              ...currentPolicy,
                                              billingRules: {
                                                ...(currentPolicy?.billingRules || {}),
                                                [key]: e.target.checked,
                                              },
                                            },
                                          };
                                        });
                                      }}
                                      disabled={!canEditOrgInternalPolicy}
                                      className="w-4 h-4"
                                    />
                                    {bl}
                                  </label>
                                ))}
                              </div>

                              {/* SECTION 5 — SERVICE EXPANSION / CHANGE REQUESTS */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Section 5 — Service Expansion / Change Requests</h5>
                                <p className="text-xs text-gray-500 mb-3">
                                  Control whether facility admins can ask Stratel to add or turn off services for their own facility.
                                  Requests the organization would pay for always need your approval. You are told when a facility asks to turn off a service the organization added.
                                </p>
                                <label className="flex items-center gap-3 text-sm text-gray-700 py-2">
                                  <input type="checkbox" checked={Boolean(sep.facilityCanRequest)} onChange={(e) => canEditOrgInternalPolicy && setServiceExpansionPolicy('facilityCanRequest', e.target.checked)} disabled={!canEditOrgInternalPolicy} className="w-4 h-4" />
                                  Facilities can request service expansions / changes
                                </label>
                                {sep.facilityCanRequest && (
                                  <label className="flex items-center gap-3 text-sm text-gray-700 py-2 ml-6">
                                    <input type="checkbox" checked={Boolean(sep.requiresOrgApproval)} onChange={(e) => canEditOrgInternalPolicy && setServiceExpansionPolicy('requiresOrgApproval', e.target.checked)} disabled={!canEditOrgInternalPolicy} className="w-4 h-4" />
                                    Require my approval even when the facility pays
                                  </label>
                                )}
                              </div>

                              {canEditOrgInternalPolicy && (
                                <div>
                                  <button type="button" onClick={saveOrgInternalPolicy} disabled={orgSaving} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
                                    {orgSaving ? 'Saving...' : 'Save Policy'}
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {/* ══ FACILITY ADMIN VIEW ══ */}
                          {!isOrgAdminSettings && (
                            <>
                              {/* SECTION 1 — FACILITY USER ACCESS */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Facility User Access</h5>
                                <p className="text-xs text-gray-500 mb-3">Assign access for your facility users in areas your organization has marked as Facility Controlled.</p>

                                {fca.lms === 'facility' ? (
                                  <div className="mb-4">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Training</p>
                                    <PolicyRow label="Can assign LMS courses" modeValue={facilityPolicyDraft.internalAccessPolicy?.lms?.assignmentMode || 'inherit'} onModeChange={(v) => setFacilityPolicyMode('lms', 'assignmentMode', v)} roles={facilityPolicyDraft.internalAccessPolicy?.lms?.assignmentRoles} onToggleRole={(r, c) => toggleFacilityPolicyRole('lms', 'assignmentRoles', r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.lms?.assignmentUserIds} onSetUsers={(ids) => setFacilityPolicyUsers('lms', 'assignmentUserIds', ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                  </div>
                                ) : (
                                  <div className="mb-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded p-2">
                                    <span className="font-medium">Training</span> — managed by your organization.
                                  </div>
                                )}

                                {fca.staffing === 'facility' ? (
                                  <div className="mb-4">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Staffing</p>
                                    <PolicyRow label="Can submit staffing requests" modeValue={facilityPolicyDraft.internalAccessPolicy?.staffing?.requesterMode || 'inherit'} onModeChange={(v) => setFacilityPolicyMode('staffing', 'requesterMode', v)} roles={facilityPolicyDraft.internalAccessPolicy?.staffing?.requesterRoles} onToggleRole={(r, c) => toggleFacilityPolicyRole('staffing', 'requesterRoles', r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.staffing?.requesterUserIds} onSetUsers={(ids) => setFacilityPolicyUsers('staffing', 'requesterUserIds', ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                  </div>
                                ) : (
                                  <div className="mb-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded p-2">
                                    <span className="font-medium">Staffing</span> — managed by your organization.
                                  </div>
                                )}

                                {fca.consulting === 'facility' ? (
                                  <div className="mb-4">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Consulting</p>
                                    <PolicyRow label="Can manage consulting collaborators" modeValue={facilityPolicyDraft.internalAccessPolicy?.consulting?.collaboratorMode || 'inherit'} onModeChange={(v) => setFacilityPolicyMode('consulting', 'collaboratorMode', v)} roles={facilityPolicyDraft.internalAccessPolicy?.consulting?.collaboratorRoles} onToggleRole={(r, c) => toggleFacilityPolicyRole('consulting', 'collaboratorRoles', r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.consulting?.collaboratorUserIds} onSetUsers={(ids) => setFacilityPolicyUsers('consulting', 'collaboratorUserIds', ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                  </div>
                                ) : (
                                  <div className="mb-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded p-2">
                                    <span className="font-medium">Consulting</span> — managed by your organization.
                                  </div>
                                )}

                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rate Approvals</p>
                                  <PolicyRow label="Can access rate approvals" modeValue={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.accessMode || 'inherit'} onModeChange={(v) => setFacilityPolicyMode('rateApprovals', 'accessMode', v)} roles={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.accessRoles} onToggleRole={(r, c) => toggleFacilityPolicyRole('rateApprovals', 'accessRoles', r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.accessUserIds} onSetUsers={(ids) => setFacilityPolicyUsers('rateApprovals', 'accessUserIds', ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                  <PolicyRow label="Can approve rates" modeValue={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.approveMode || 'inherit'} onModeChange={(v) => setFacilityPolicyMode('rateApprovals', 'approveMode', v)} roles={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.approveRoles} onToggleRole={(r, c) => toggleFacilityPolicyRole('rateApprovals', 'approveRoles', r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.rateApprovals?.approveUserIds} onSetUsers={(ids) => setFacilityPolicyUsers('rateApprovals', 'approveUserIds', ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                </div>
                              </div>

                              {/* SECTION 2 — WORKER MATCH REJECTION CAP */}
                              <div className="rounded-lg border border-gray-200 p-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Worker Match Rejection Cap</h5>
                                <p className="text-xs text-gray-500 mb-3">View only. Set by Stratel. Your team can view this cap but cannot change it.</p>
                                <div className="flex items-center gap-4">
                                  <div className="w-24 border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 text-center font-semibold">
                                    {Number(pol?.staffing?.matchRejectionCap ?? 3)}
                                  </div>
                                  <p className="text-xs text-gray-500">rejections per requested position before escalation</p>
                                </div>
                              </div>

                              {/* SECTION 3 — BILLING ACCESS */}
                              {(() => {
                                const enabledBillingScopes = [
                                  { label: 'Can view invoices when facility is the billing party', scope: 'viewInvoicesFacilityParty', enabledKey: 'viewInvoicesFacilityPartyEnabled' },
                                  { label: 'Can view invoices when organization is the billing party', scope: 'viewInvoicesOrgParty', enabledKey: 'viewInvoicesOrgPartyEnabled' },
                                  { label: 'Can view payment status when organization is the billing party', scope: 'viewPaymentStatusOrgParty', enabledKey: 'viewPaymentStatusOrgPartyEnabled' },
                                  { label: 'Can pay invoices when facility is the billing party', scope: 'payInvoicesFacilityParty', enabledKey: 'payInvoicesFacilityPartyEnabled' },
                                ].filter(({ enabledKey }) => Boolean(pol?.billingRules?.[enabledKey]));
                                return enabledBillingScopes.length > 0 ? (
                                  <div className="rounded-lg border border-gray-200 p-4">
                                    <h5 className="text-sm font-semibold text-gray-900 mb-0.5">Billing Access</h5>
                                    <p className="text-xs text-gray-500 mb-3">Assign which facility users can view or act on billing items enabled for your facility.</p>
                                    {enabledBillingScopes.map(({ label: bl, scope }) => (
                                      <PolicyRow key={scope} label={bl} modeValue={facilityPolicyDraft.internalAccessPolicy?.billing?.[`${scope}Mode`] || 'inherit'} onModeChange={(v) => setFacilityPolicyValue('billing', `${scope}Mode`, v)} roles={facilityPolicyDraft.internalAccessPolicy?.billing?.[`${scope}Roles`]} onToggleRole={(r, c) => toggleFacilityPolicyRole('billing', `${scope}Roles`, r, c)} userIds={facilityPolicyDraft.internalAccessPolicy?.billing?.[`${scope}UserIds`]} onSetUsers={(ids) => setFacilityPolicyUsers('billing', `${scope}UserIds`, ids)} disabled={!canEditFacilityInternalPolicy} modeOptions={[{ value: 'inherit', label: 'Inherit org policy' }, { value: 'role_based', label: 'By Role' }, { value: 'specific_users', label: 'By Person' }]} roleOptions={[{ key: 'facility_admin', label: 'Facility Admin' }, { key: 'facility_manager', label: 'Facility Manager' }]} />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                                    <h5 className="text-sm font-semibold text-gray-500 mb-1">Billing Access</h5>
                                    <p className="text-xs text-gray-400">No billing permissions have been enabled for facilities by your organization.</p>
                                  </div>
                                );
                              })()}

                              {/* SECTION 4 — SERVICE EXPANSION */}
                              <div className={`rounded-lg p-4 ${sep.facilityCanRequest ? 'border border-gray-200' : 'border border-gray-100 bg-gray-50'}`}>
                                <h5 className={`text-sm font-semibold mb-1 ${sep.facilityCanRequest ? 'text-gray-900' : 'text-gray-500'}`}>Service Expansion / Change Requests</h5>
                                {sep.facilityCanRequest ? (
                                  <>
                                    <p className="text-xs text-gray-500 mb-2">Your organization has enabled facilities to request service expansions or changes.</p>
                                    {sep.requiresOrgApproval && (
                                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Requests require organization approval before submission to Stratel.</p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-xs text-gray-400">Service expansion requests are not enabled for facilities at this time. Contact your organization administrator.</p>
                                )}
                              </div>

                              {canEditFacilityInternalPolicy && (
                                <button type="button" onClick={saveFacilityPolicy} disabled={savingFacilityPolicy} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
                                  {savingFacilityPolicy ? 'Saving...' : 'Save Facility Policy'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}


                <div className="flex gap-3 pt-4">
                  {activeOrgSubtab !== 'service-access' && activeOrgSubtab !== 'internal-access-policy' && (
                    <button type="submit" disabled={orgSaving || (!isOrgAdminSettings && savingFacilityInfo)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                      {(orgSaving || (!isOrgAdminSettings && savingFacilityInfo)) ? 'Saving...' : isOrgAdminSettings ? 'Save Organization' : 'Save Facility'}
                    </button>
                  )}
                  <Link to="/staffing/rate-approvals" className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Manage Rate Approvals
                  </Link>
                </div>
              </form>
            </div>
          ) : (
            <div className="p-4 text-gray-500">{orgHeadingLabel} not found or you do not have access.</div>
          )}
        </div>
      )}
    </div>
  );
}
